// Component: SmartChart.jsx
// Purpose: Render historical and live market data while preserving chart state.

import React, { useEffect, useRef, useState, useCallback, useMemo, memo } from 'react';
import { createChart, ColorType, CrosshairMode, LineStyle, LineType, LineSeries, AreaSeries, CandlestickSeries, HistogramSeries, createSeriesMarkers } from 'lightweight-charts';
import { Tooltip } from '../ds/Tooltip';
import { FiBarChart2, FiActivity, FiLayers, FiTrendingUp, FiChevronsRight } from 'react-icons/fi';
import { services } from '../../services/api';
import moment from 'moment-timezone';
import { getExchangeTimezoneSync } from '../../utils/timezone';
import Loader from '../Loader';
import { useIndicators } from './useIndicators';
import { useChartData } from './useChartData';
import { useLiveUpdates } from './useLiveUpdates';
import { barVolume, extractFieldValue, mergeLiveTail, sanitizeChartData, ChartSyncService, anchorRange, rebaseRange, isSameRange, getTrendColor, PRICE_SCALE_WIDTH, MAIN_CANVAS_MIN_PX, PANE_HEIGHT, PANE_STRIP_HEIGHT, MAX_COLLAPSED_STRIPS } from './utils';
import { layoutMarkerLabels, makeTextMeasurer, LEVEL_TITLE_PAD_PX } from './markerLabelLayout';
import IndicatorPane from './IndicatorPane';
import IndicatorOverlay from './IndicatorOverlay';
import ChandelierOverlay from './ChandelierOverlay';
import TrendSlopeOverlay from './TrendSlopeOverlay';
import { buildTradeSpanMap, findTradeAtTime, getLevelDisplayConfig } from '../../utils/tradeLevelUtils';
import { toUnixSec } from '../../utils/tradeMarkerBuilder';
import TradeLevelOverlay from './TradeLevelOverlay';
import { resolveMarkerStates } from '../../pages/Strategies/Modern/StrategyBuilder/utils/markerDefaults';
import { getDrawnOutputKeys, expandScalarPoints } from '../../pages/Strategies/Modern/StrategyBuilder/config/nodeVisualConfig';

// --> Optimization: Custom Deep Compare for Stability
const isDeepEqual = (a, b) => {
    if (a === b) return true;
    if (!a || !b || typeof a !== 'object' || typeof b !== 'object') return false;
    const keysA = Object.keys(a);
    const keysB = Object.keys(b);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
        if (!keysB.includes(key) || !isDeepEqual(a[key], b[key])) return false;
    }
    return true;
};

// --> Smooth animated zoom transition for trade/period navigation.
//    Operates in LOGICAL index space (not time space) to prevent
//    "right should be >= left" assertion errors.  The time-based
//    setVisibleRange converts timestamps to logical indices in a deferred
//    draw frame where race conditions can invert the range.
//    setVisibleLogicalRange stores indices directly — no async conversion.
//    Uses a module-level counter so only the most recent animation runs —
//    previous animations are silently cancelled on the next frame.
//--> Indicators drawn in their own sub-pane below the main chart (one filter for the render AND
//*   the main chart's time-axis visibility, so the two can never disagree).
const isSubPaneIndicator = (i) => !i.overlay && !i.isHidden &&
    i.styleOptions?.visualType?.toLowerCase() !== 'custom' &&
    i.styleOptions?.type?.toLowerCase() !== 'custom' &&
    i.styleOptions?.chartType?.toLowerCase() !== 'custom' &&
    i.styleOptions?.chartType?.toLowerCase() !== 'marker' &&
    !['comparator', 'logic_gate'].includes(i.nodeType); // PROTOCOL: Never render these in sub-panes

const ZOOM_ANIM_DURATION_MS = 300;
let activeAnimationId = 0;
const animateVisibleRange = (chart, targetFrom, targetTo, onDone) => {
    const myId = ++activeAnimationId;
    const ts = chart.timeScale();

    //--> Convert target timestamps to logical bar indices.
    //    timeToIndex(t, true) returns nearest index clamped to [0, length-1].
    const targetFromIdx = ts.timeToIndex(targetFrom, true);
    const targetToIdx   = ts.timeToIndex(targetTo, true);

    //--> Fallback: if chart has no data or conversion fails, just fitContent.
    if (targetFromIdx === null || targetToIdx === null) {
        try { ts.fitContent(); } catch (_) {}
        onDone?.();
        return;
    }

    //--> Add logical padding for any time that extends beyond data bounds.
    //    timeToIndex clamps to data bounds, so we approximate the overshoot
    //    as extra logical bars using the average bar spacing.
    const curLogical = ts.getVisibleLogicalRange();
    let tgtFrom = targetFromIdx;
    let tgtTo   = targetToIdx;

    //--> Ensure minimum 1-bar range to avoid from === to
    if (tgtTo <= tgtFrom) tgtTo = tgtFrom + 1;

    if (!curLogical) {
        //--> Chart hasn't rendered yet — snap directly, no animation.
        try { ts.setVisibleLogicalRange({ from: tgtFrom, to: tgtTo }); } catch (_) {}
        onDone?.();
        return;
    }

    const startFrom = curLogical.from;
    const startTo   = curLogical.to;
    const t0 = performance.now();

    const step = (now) => {
        if (myId !== activeAnimationId) return; //--> Superseded by newer animation
        try {
            const elapsed = now - t0;
            const t = Math.min(elapsed / ZOOM_ANIM_DURATION_MS, 1);

            if (t >= 1) {
                //--> Final frame: set exact target
                ts.setVisibleLogicalRange({ from: tgtFrom, to: tgtTo });
                onDone?.();
                return;
            }

            //--> easeOutCubic: fast start, gentle landing
            const ease = 1 - Math.pow(1 - t, 3);
            const from = startFrom + (tgtFrom - startFrom) * ease;
            const to   = startTo   + (tgtTo   - startTo)   * ease;
            //--> Safety: ensure from < to at every frame
            if (to > from) {
                ts.setVisibleLogicalRange({ from, to });
            }
            requestAnimationFrame(step);
        } catch (_) {
            //--> Chart likely destroyed mid-animation — stop silently
        }
    };
    requestAnimationFrame(step);
};

const useDeepCompareMemoize = (value) => {
  const ref = useRef();
  // We use a ref to hold the last value that was verified as equal
  // If the new value is NOT deeply equal, we update the ref
  if (!isDeepEqual(value, ref.current)) {
    ref.current = value;
  }
  return ref.current;
}

/**
 * ChartErrorBoundary: Catches errors in the ChartPane (e.g. "Content limit" crash)
 * allowing the rest of the UI to function.
 */
class ChartErrorBoundary extends React.Component {
    constructor(props) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error) {
        return { hasError: true, error };
    }

    componentDidCatch(error, errorInfo) {
        console.error("[SmartChart] Error Boundary caught error:", error, errorInfo);
    }

    handleRetry = () => {
        this.setState({ hasError: false, error: null });
        if (this.props.onRetry) {
             this.props.onRetry();
        }
    }

    render() {
        if (this.state.hasError) {
            return (
                <div className={`w-full flex flex-col items-center justify-center bg-gray-50 dark:bg-bg-2 rounded-lg border border-red-200 dark:border-red-900/30 p-6 ${this.props.heightClass || 'h-[50vh]'}`}>
                    <div className="text-red-500 mb-2">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-10 w-10" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                           <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <h3 className="text-gray-800 dark:text-gray-200 font-bold mb-1">Chart Render Error</h3>
                    <p className="text-gray-500 dark:text-gray-400 text-xs text-center mb-4 max-w-xs">
                        Something went wrong while displaying the chart.
                    </p>
                    <button 
                        onClick={this.handleRetry}
                        className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg shadow transition-colors"
                    >
                        Retry Chart
                    </button>
                    <div className="mt-4 p-2 bg-gray-100 dark:bg-black/20 rounded text-[10px] text-gray-400 font-mono w-full overflow-hidden text-ellipsis whitespace-nowrap text-center opacity-50 hover:opacity-100 transition-opacity">
                        {this.state.error?.message || "Unknown Error"}
                    </div>
                </div>
            );
        }
        return this.props.children;
    }
}



/**
 * ChartPane: The core charting component.
 * Handles a single lightweight-chart instance, data fetching, and updates.
 */

// Native Enums from lightweight-charts v5 are used directly

//! 50-13 — last marker signature handed to each marker plugin; setMarkers runs only when it changes.
const markerSigByPlugin = new WeakMap();
const MARKER_FONT = '12px -apple-system, BlinkMacSystemFont, sans-serif';

const ChartPane = ({
    instrument,
    liveTick,
    liveNodeResults = null,
    liveBarTimeframe = null,
    instruments,
    general,
    mode = 'live',
    dateRange = null,
    fetchDateRange = null,
    rolloverDays = 0,
    //! LB-8 (F-06) — structural companion to rolloverDays: threaded down to
    //! resolveRolloverDays() so a PINNED (isContinuous=false) caller can never
    //! resolve a nonzero rollover_days, even if instrument.rollover_days is set.
    isContinuous,
    heightClass = 'h-[50vh]',
    user,
    id,
    isMultiChart = false,
    onCrosshairMove,
    externalCrosshair,
    initialTimeframe = '1m',
    timeframe: propsTimeframe, // Rename to avoid conflict with state
    showToolbar = true,
    overlays = [],
    onTimeframeChange,
    chartType = 'candle',
    styleOptions = {},
    layers = [],
    externalIndicators = [], // New Prop: External indicators from parent (Renamed to avoid conflict)
    nodes = [], // Full Graph Nodes
    edges = [], // Full Graph Edges
    onDataLoaded,
    onChartTypeChange,
    availableTypes, // Optional: restrict toolbar options to these types
    onError, // New Prop: Error callback
    onIndicatorsUpdate, // New Prop: Notify parent of indicator results
    replayMode = null,
    replayRunId = null,
    onBacktestRequired = null,
    tradeMarkers = [],    // Trade execution markers (entry/exit) from backtest or live
    levelHistory = null,       // Array of trades with level_history, or null
    levelDisplayNodes = null,  // Strategy graph nodes for resolving level_display config
    tickType = null,           // Tick source: 'LIVE' | 'SIMULATED' | 'HISTORIC_OHLC' | null
    lastEvalTime = undefined,  // Latest bar-close time for live node_results (ms, sec, or ISO)
    resolvedInstrumentId = null,
    onGapRepair = null,
    canvasAction = null, // optional host control (e.g. expand/collapse) drawn inside the main canvas, top-right
    compactPanes = false, // 50-11: collapsed card — each sub-pane is a fixed strip, stack never scrolls
    onPaneActivate = null, // 50-11: strip / '+N more' activation (host expands the card)
    paneStripMode = 'rows', // 50-12: 'tabs' = collapsed card shows ONE 44px tab row (host decides tier; SmartChart stays tier-ignorant)
}) => {
  const chartContainerRef = useRef();
  const chartInstanceRef = useRef(null);
  
    // --> Exchange timezone for date-range labels (chart x-axis already shows correct
    //     face-clock values because backend timestamps are naive exchange-local datetimes
    //     interpreted as UTC via moment.utc())
    const exchangeTz = useMemo(() => getExchangeTimezoneSync(instrument?.exchange), [instrument?.exchange]);

    const [activeLayerId, setActiveLayerId] = useState((layers && layers.length > 0) ? layers[0].id : 'default');

    const toDateFromChartTime = useCallback((time) => {
        if (typeof time === 'number') return new Date(time * 1000);
        if (time && typeof time === 'object' && typeof time.year === 'number') {
            return new Date(Date.UTC(time.year, (time.month || 1) - 1, time.day || 1));
        }
        return null;
    }, []);

    //--> Format time for x-axis tick marks (receives tickMarkType from lightweight-charts)
    // tickMarkType: 0=Year, 1=Month, 2=DayOfMonth, 3=Time, 4=TimeWithSeconds
    const formatTickMark = useCallback((time, tickMarkType) => {
        const d = toDateFromChartTime(time);
        if (!d || Number.isNaN(d.getTime())) return '';

        // Daily timeframe: show date
        if (typeof time !== 'number') {
            return new Intl.DateTimeFormat('en-IN', {
                timeZone: exchangeTz,
                day: '2-digit',
                month: 'short',
            }).format(d);
        }

        // Intraday: show date at day/month/year boundaries, time otherwise
        if (tickMarkType !== undefined && tickMarkType <= 2) {
            // Day, Month, or Year boundary → show date
            return new Intl.DateTimeFormat('en-IN', {
                timeZone: exchangeTz,
                day: '2-digit',
                month: 'short',
            }).format(d);
        }

        // Regular time tick
        return new Intl.DateTimeFormat('en-IN', {
            timeZone: exchangeTz,
            hour: '2-digit',
            minute: '2-digit',
            hour12: false,
        }).format(d);
    }, [exchangeTz, toDateFromChartTime]);

    //--> Format time for crosshair tooltip — always show date + time
    const formatChartTimeInExchangeTz = useCallback((time) => {
        const d = toDateFromChartTime(time);
        if (!d || Number.isNaN(d.getTime())) return '';

        if (typeof time === 'number') {
            return new Intl.DateTimeFormat('en-IN', {
                timeZone: exchangeTz,
                day: '2-digit',
                month: 'short',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
            }).format(d);
        }

        return new Intl.DateTimeFormat('en-IN', {
            timeZone: exchangeTz,
            day: '2-digit',
            month: 'short',
        }).format(d);
    }, [exchangeTz, toDateFromChartTime]);

    // --> Performance: Deep Memoize Props to avoid JSON.stringify in dependencies
    const memoizedLayers = useDeepCompareMemoize(layers);
    const memoizedStyleOptions = useDeepCompareMemoize(styleOptions);

    // --> Optimization: Selective Memoization for Graph Nodes
    // We strip UI-only properties (position, dragging, selection) to prevent chart re-renders during drag operations.
    // Also strip level_display from visual_settings — it's a chart overlay concern that must NOT trigger indicator recalculation.
    const getNodeSignature = (nodeList) => {
        if (!nodeList) return [];
        return nodeList.map(n => {
            const { visual_settings, ...restData } = (n.data || {});
            const { level_display: _ld, ...restVisualSettings } = (visual_settings || {});
            return {
                id: n.id,
                type: n.type,
                data: { ...restData, visual_settings: restVisualSettings },
                // EXCLUDE: position, measured, selected, dragging, width, height, handles
            };
        });
    };

    const calculationNodes = useMemo(() => getNodeSignature(nodes), [nodes]);
    const memoizedNodes = useDeepCompareMemoize(calculationNodes);

    //--> Graph-node id set — used by useLiveUpdates to gate local indicator
    //*   recomputation in live/paper mode. indicatorMaps is the authoritative
    //*   source for these COMPUTE nodes (indicators / math / comparator / etc.).
    //*
    //*   CRITICAL: visual presentation nodes (candle_node / area_node /
    //*   heikin_node / line_node / volume_node / chart_output / chart_node)
    //*   MUST be excluded. Their data flow is OHLC-from-bar via the tick
    //*   handler's update() path, NOT via liveNodeResults — visual nodes
    //*   don't compute values, they render the bar's OHLC. Including them
    //*   here causes the gate at useLiveUpdates.js:81 to early-return
    //*   without updating their series — verified empirically during the
    //*   2026-05-06 RCA (no live candles formed for chart_output graphs
    //*   with a candle_node wired in; gate fired every tick with no
    //*   liveNodeResults entry for the visual node, swallowing OHLC update).
    const VISUAL_NODE_TYPES = new Set([
        'candle_node', 'area_node', 'heikin_node', 'line_node',
        'volume_node', 'chart_output', 'chart_node',
    ]);
    const graphNodeIds = useMemo(
        () => new Set((nodes || []).filter(n => !VISUAL_NODE_TYPES.has(n?.type)).map((n) => n.id)),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [nodes],
    );
    
    const memoizedEdges = useDeepCompareMemoize(edges);
    const memoizedExternalIndicators = useDeepCompareMemoize(externalIndicators);

    // Update active layer if layers prop changes (e.g. new node connected)
    useEffect(() => {
        if (layers && layers.length > 0 && !layers.find(l => l.id === activeLayerId)) {
            setActiveLayerId(layers[0].id);
        }
    }, [layers, activeLayerId]);

    // Crosshair state for overlays

 
    const mainWrapperRef = useRef(null);
    const mainSeriesRef = useRef(null);
    const tradeMarkersPluginRef = useRef(null);
    // --> 50-13 marker label layout (refs only, no state): built marker sources, hover index, tip node
    const tradeBuiltMarkersRef = useRef([]);
    const attachedMarkersRef = useRef(new Map()); // series -> built markers (ONE publish path from AttachedModeManager)
    const markerHoverRef = useRef({});
    const markerPaneHeightRef = useRef(undefined);
    const markerHoverKeyRef = useRef(null);
    const markerTipRef = useRef(null);
    const markerFrameRef = useRef(null);
    const markerMeasureRef = useRef(null);
    const scheduleMarkerRelayoutRef = useRef(null);
    const seriesRefs = useRef([]); 
    const volumeSeriesRef = useRef(null);

    const visibleLogicalRangeRef = useRef(null); 
    const indicatorSeriesMap = useRef(new WeakMap());
    const indicatorIdToSeriesMap = useRef(new Map()); // Reverse map for ID -> Series lookup
    const [registryVersion, setRegistryVersion] = useState(0); // Trigger re-syncs when series are registered

    // Refs to access latest state inside closures
    const activeIndicatorsRef = useRef([]);
    const calculateIndicatorsRef = useRef(() => []);
    const resolveEnrichedPayloadRef = useRef(null); // Helper for sync coordination
    const timeToDataMapRef = useRef(new Map()); // O(1) Lookup for enrichment perf


    //--> Build trade span map for level visualization
    const tradeSpanMap = useMemo(() => {
        if (!levelHistory) return [];
        return buildTradeSpanMap(levelHistory);
    }, [levelHistory]);

    //--> Resolve level display configs from exit nodes (stabilized to avoid chart redraws)
    const levelDisplayConfigsRef = useRef({});
    const levelDisplayConfigs = useMemo(() => {
        if (!levelDisplayNodes) return levelDisplayConfigsRef.current;
        const configs = {};
        for (const node of levelDisplayNodes) {
            const action = node.data?.action;
            if (['sl', 'tp', 'time_exit'].includes(action)) {
                configs[node.id] = getLevelDisplayConfig(node);
            }
        }
        //--> Only return a new object reference if configs actually changed
        const prev = levelDisplayConfigsRef.current;
        const nextJson = JSON.stringify(configs);
        if (nextJson === JSON.stringify(prev)) return prev;
        levelDisplayConfigsRef.current = configs;
        return configs;
    }, [levelDisplayNodes]);

    //--> Determine if always-visible mode is enabled (any config has hover_only=false)
    const isAlwaysVisible = Object.values(levelDisplayConfigs).some(c => c.enabled && !c.hover_only);

    const [activeTradeContext, setActiveTradeContext] = useState(null);
    const activeTradeRef = useRef(null); // the trade currently in state (identity), so hover commits only on change

    //--> Ref for tradeSpanMap to avoid re-subscribing crosshair on trade changes
    const tradeSpanMapRef = useRef([]);
    useEffect(() => { tradeSpanMapRef.current = tradeSpanMap; }, [tradeSpanMap]);

    const handleIndicatorSeriesCreated = useCallback((id, series, key = 'value') => {
        if (series) {
            // Save the key onto the series instance so useLiveUpdates can extract the correct field from an object result
            series.seriesKey = key;

            // Maintain an array of ALL series instances for this ID to support multi-line indicators
            if (!indicatorIdToSeriesMap.current.has(id)) {
                indicatorIdToSeriesMap.current.set(id, []);
            }
            const existing = indicatorIdToSeriesMap.current.get(id);
            if (!existing.includes(series)) {
                existing.push(series);
            }
            indicatorSeriesMap.current.set(series, id);
        } else {
            // Unmount: remove all for now
            indicatorIdToSeriesMap.current.delete(id);
        }
        setRegistryVersion(v => v + 1);
    }, []);

    // CENTRALIZED PAYLOAD RESOLVER (High-Perf O(1))
    const resolveEnrichedPayload = useCallback((param, sourceId) => {
        if (!param || !param.time) return null;

        // Unified Unix Timestamp for $O(1)$ lookup across all datasets
        // For object time (daily candles), convert to UTC unix to match the stored map keys
        const timeKey = typeof param.time === 'object' ? 
            moment.utc({ year: param.time.year, month: param.time.month - 1, day: param.time.day }).unix() : 
            param.time;

        // 1. Resolve 'fullData' (OHLCV) via O(1) Map Lookup
        let fullData = {};
        if (timeToDataMapRef.current) {
            const candle = timeToDataMapRef.current.get(timeKey);
            if (candle) fullData = { ...candle };
        }

        const indicatorValues = {};
        
        // 2. Get Overlay Values (Fast O(1) from Series Map)
        if (param.seriesData && indicatorSeriesMap.current) {
            param.seriesData.forEach((value, series) => {
                if (indicatorSeriesMap.current.has(series)) {
                    const id = indicatorSeriesMap.current.get(series);
                    let v = value.value !== undefined ? value.value : value;
                    indicatorValues[id] = v;
                }
            });
        }

        // 3. Get Pane/Other Values (Fallback/Complete Lookup)
        if (activeIndicatorsRef.current && activeIndicatorsRef.current.length > 0) {
            const indicators = activeIndicatorsRef.current;
            const len = indicators.length;
            for (let i = 0; i < len; i++) {
                const ind = indicators[i];

                //--> Always fetch the original multi-output dict if available.
                //--> Lightweight Charts param.seriesData only gives us scalars for sub-series.
                const existing = indicatorValues[ind.id];
                // Only skip if the existing value is an explicit multi-key object (OHLC or multi-output dict).
                if (existing !== undefined && existing !== null && typeof existing === 'object'
                    && !Array.isArray(existing) && Object.keys(existing).length > 1) {
                    continue;
                }

                // PERFORMANCE: Pass targetTime (timeKey) for O(1) lookup in useIndicators
                // Pass fullData (the current candle) so Field Extractors can resolve values
                const val = calculateIndicatorsRef.current(ind, fullData, timeKey);
                if (val !== undefined && val !== null) {
                    indicatorValues[ind.id] = val;
                }
            }
        }

        return {
            time: param.time,
            point: param.point,
            sourceId: sourceId || param.sourceId || id,
            ...fullData,
            ...indicatorValues
        };
    }, [id]);

    // SYNC STATE: Performance Optimized (Via Service)
    // ----------------------------------------------------
    
    // Performance Optimization: Use Event Emitter for Crosshair & Zoom/Pan to avoid 60fps re-renders
    // Initialize with ID for BroadcastChannel support
    const syncService = useMemo(() => new ChartSyncService(id || 'default'), [id]);

    // Clear stale sync range from previous component mounts sharing the same broadcast channel.
    // This fixes the bug where switching history runs applies the old run's zoom to sub-charts.
    const hasClearedStaleSyncRef = useRef(false);
    if (!hasClearedStaleSyncRef.current) {
        if (syncService) syncService.lastRange = null;
        hasClearedStaleSyncRef.current = true;
    }

    // NOTE: syncedLogicalRange State removed in favor of direct service subscription
    // handleSyncRange removed - children emit directly to service

    // Sync Lock
    const appliedSyncRangeRef = useRef(null); //! last synced range applied here — its echo is not re-broadcast (utils.js isSameRange)
    const syncAnchorTimeRef = useRef(null); // first bar handed to the main series — stamps emitted ranges

    // Effect: Apply Synced Range to Main Chart (from Service)
    useEffect(() => {
        if (!syncService) return;

        //--> Main chart receives logical ranges (from BroadcastChannel cross-window sync).
        //--> Logical ranges preserve whitespace beyond data bounds (pan/zoom fidelity).
        const unsubscribe = syncService.subscribeRange((incoming) => {
            //* rebased onto THIS chart's bar indices (utils.js anchorRange); null = measured on other data
            const range = rebaseRange(incoming, chartInstanceRef.current?.timeScale());
            if (chartInstanceRef.current && range) {
                const currentRange = chartInstanceRef.current.timeScale().getVisibleLogicalRange();
                if (!isSameRange(currentRange, range)) {
                    appliedSyncRangeRef.current = range;
                    chartInstanceRef.current.timeScale().setVisibleLogicalRange(range);
                }
            }
        });
        return () => {
             unsubscribe();
             // Don't destroy service here if it's memoized/shared, but here we created it so we own it.
             // Actually, if we own it, we should destroy it on unmount only.
             // syncService.destroy() is called in clean-up of the memo creation or a separate effect? 
             // The original code destroyed it here. Keeping behavior.
             syncService.destroy(); 
        };
    }, [syncService]);

    // ----------------------------------------------------
    // HOOK: Historical Data Management
    // ----------------------------------------------------
    const [timeframe, setTimeframe] = useState(initialTimeframe);

    // Sync internal timeframe with external prop if provided
    useEffect(() => {
        if (typeof propsTimeframe !== 'undefined' && propsTimeframe !== timeframe) {
            setTimeframe(propsTimeframe);
        }
    }, [propsTimeframe]);

    const {
        data,
        setData,
        rawHistory,
        loading,
        loadingMessage,
        setLoadingMessage,
        setLoading, // Exposed for manual download
        error,
        setError,
        fetchHistory,
        dataRef,
        paginationState, // NEW: pagination metadata (currentPage, totalPages, hasPrev, hasNext)
        loadNextPage,    // NEW: callback to load next page of OHLC
        loadPreviousPage, // NEW: callback to load previous page of OHLC
        loadPage          // Direct page load for targeted jumps (period selection)
    } = useChartData({
        instrument,
        timeframe,
        mode: replayMode ? 'backtest' : mode,
        dateRange,
        fetchDateRange,
        rolloverDays,
        isContinuous,
        resolvedInstrumentId,
        onGapRepair,
    });

    //--> Ref-mirror for pagination so scroll handler reads latest without triggering re-subscription
    const paginationStateRef = useRef(paginationState);
    //--> Track which edge triggered pagination load ('left' | 'right' | null) for subtle edge indicator
    const [paginationLoadDirection, setPaginationLoadDirection] = useState(null);
    useEffect(() => {
        paginationStateRef.current = paginationState;
    }, [paginationState]);

    //--> Stable refs for page-load callbacks (avoids scroll effect re-subscription on every useCallback identity change)
    const loadNextPageRef = useRef(loadNextPage);
    const loadPreviousPageRef = useRef(loadPreviousPage);
    const loadPageRef = useRef(loadPage);
    useEffect(() => { loadNextPageRef.current = loadNextPage; }, [loadNextPage]);
    useEffect(() => { loadPreviousPageRef.current = loadPreviousPage; }, [loadPreviousPage]);
    useEffect(() => { loadPageRef.current = loadPage; }, [loadPage]);

    const [chartInstance, setChartInstance] = useState(null);

    const handleTimeframeChange = (newTf) => {
        setTimeframe(newTf);
        if (onTimeframeChange) onTimeframeChange(newTf);
    };

    const timeframes = ['1m', '5m', '15m', '1h', '1D'];
    //--> Reactive theme flag: watch the <html> class so the chart re-themes on a
    //    LIVE theme toggle, not just on refresh. SmartChart doesn't read the app
    //    context (it's reused outside it), so observe the DOM directly.
    const [isDarkMode, setIsDarkMode] = useState(() => document.documentElement.classList.contains('dark'));
    useEffect(() => {
        const el = document.documentElement;
        const obs = new MutationObserver(() => setIsDarkMode(el.classList.contains('dark')));
        obs.observe(el, { attributes: true, attributeFilter: ['class'] });
        return () => obs.disconnect();
    }, []);
    
    // ----------------------------------------------------
    // CHART LIFECYCLE: Initialization & Resize
    // ----------------------------------------------------
    useEffect(() => {
        if (!chartContainerRef.current) return;

        try {
            let chart = chartInstanceRef.current; // Re-use chart if exists

            if (!chart) {
                chart = createChart(chartContainerRef.current, {
                layout: {
                    background: { type: ColorType.Solid, color: 'transparent' },
                    textColor: isDarkMode ? '#a3a3a3' : '#3c4350',
                    attributionLogo: false,
                },
                grid: {
                    vertLines: { color: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)' },
                    horzLines: { color: isDarkMode ? 'rgba(255, 255, 255, 0.06)' : 'rgba(0, 0, 0, 0.05)' },
                },
                crosshair: { mode: CrosshairMode.Normal },
                width: chartContainerRef.current.clientWidth || 600, 
                height: chartContainerRef.current.clientHeight || 500, 
                timeScale: {
                    timeVisible: true,
                    secondsVisible: false,
                    //! live/paper modes need right-edge breathing room so incoming bars
                    //! aren't immediately flush against the frame. backtest/rd: tight framing.
                    rightOffset: (mode === 'live' || mode === 'paper') ? 15 : 12,
                    barSpacing: 6,
                    fixLeftEdge: false,
                    lockVisibleTimeRangeOnResize: true,
                    rightBarStaysOnScroll: true,
                    borderColor: isDarkMode ? '#333333' : '#E6E6E6',
                    tickMarkFormatter: (time, tickMarkType) => formatTickMark(time, tickMarkType),
                },
                localization: {
                    timeFormatter: (time) => formatChartTimeInExchangeTz(time),
                },
                rightPriceScale: {
                    borderColor: isDarkMode ? '#333333' : '#E6E6E6',
                    scaleMargins: { top: 0.05, bottom: 0.25 }, // Top 5% margin, bottom 25% for volume
                    minimumWidth: PRICE_SCALE_WIDTH, // Fix layout scaling drift (shared with sub-panes)
                }
                });

                //--> Sync: Use LOGICAL range to keep sub-charts aligned.
                //--> Logical range preserves whitespace when panning beyond data bounds.
                //--> SyncedIndicatorWrapper pads indicator data to match main chart 1:1,
                //--> so logical indices are aligned across all charts.
                chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
                    if (range) {
                        visibleLogicalRangeRef.current = range;
                        if (syncService && !isSameRange(range, appliedSyncRangeRef.current)) {
                            appliedSyncRangeRef.current = null;
                            syncService.emitRange(anchorRange(range, chart.timeScale(), syncAnchorTimeRef.current));
                        }
                        scheduleMarkerRelayoutRef.current?.(); // 50-13: zoom / pan relayout, once per frame
                    }
                });
                
                chartInstanceRef.current = chart;
                setChartInstance(chart);
                
                const resizeObserver = new ResizeObserver(entries => {
                    if (!chartInstanceRef.current) return;
                    
                    // Optimization: Animation Frame Debounce
                    window.requestAnimationFrame(() => {
                        if (!chartInstanceRef.current) return;
                        try {
                            for (const entry of entries) {
                                const { width, height } = entry.contentRect;
                                if (width > 0 && height > 0) {
                                    chartInstanceRef.current.applyOptions({ width, height });
                                }
                            }
                            scheduleMarkerRelayoutRef.current?.(); // 50-13: pane-size change
                        } catch (err) { console.warn("[SmartChart] Resize Error:", err); }
                    });
                });
                resizeObserver.observe(chartContainerRef.current);
            } 

            // Initialize Series
            // Remove old series if structure changes
            if (seriesRefs.current.length > 0) {
                 seriesRefs.current.forEach(s => chart.removeSeries(s.instance));
                 seriesRefs.current = [];
            }
            
            if (volumeSeriesRef.current) {
                try { chart.removeSeries(volumeSeriesRef.current); } catch(e){}
                volumeSeriesRef.current = null;
            }

            const activeLayers = (layers && layers.length > 0) ? layers : [{ type: chartType, styleOptions: styleOptions }];
            seriesRefs.current = [];

            activeLayers.forEach(layer => {
                let series;
                const styles = layer.styleOptions || {};
                
                if (layer.type === 'volume') return; // Handled separately

                const priceScaleId = styles.labelPosition === 'left' ? 'left' : 'right';

                if (layer.type === 'line') {
                     series = chart.addSeries(LineSeries, { 
                         color: styles.color || '#03c9d7', 
                         lineWidth: styles.lineWidth || 2,
                         lineStyle: styles.lineStyle !== undefined ? styles.lineStyle : LineStyle.Solid,
                         lineType: styles.lineType !== undefined ? styles.lineType : LineType.Simple,
                         priceScaleId: priceScaleId
                     });
                     // Register series for Attached Mode lookup
                     if (indicatorSeriesMap.current) indicatorSeriesMap.current.set(series, layer.id);
                     if (indicatorIdToSeriesMap.current) indicatorIdToSeriesMap.current.set(layer.id, [series]);
                } else if (layer.type === 'area') {
                     series = chart.addSeries(AreaSeries, { 
                         topColor: styles.topColor || 'rgba(3, 201, 215, 0.5)',
                         bottomColor: styles.bottomColor || 'rgba(3, 201, 215, 0.0)',
                         lineColor: styles.lineColor || '#03c9d7', 
                         lineWidth: styles.lineWidth || 2,
                         lineStyle: styles.lineStyle !== undefined ? styles.lineStyle : LineStyle.Solid,
                         priceScaleId: priceScaleId 
                    });
                    // Register series for Attached Mode lookup
                    if (indicatorSeriesMap.current) indicatorSeriesMap.current.set(series, layer.id);
                    if (indicatorIdToSeriesMap.current) indicatorIdToSeriesMap.current.set(layer.id, [series]);
                } else {
                    // Candle or Heikin (both use CandlestickSeries base)
                     series = chart.addSeries(CandlestickSeries, { 
                        upColor: styles.upColor || '#00C292', 
                        downColor: styles.downColor || '#E46A76',
                        borderVisible: false, 
                        wickUpColor: styles.wickUpColor || styles.upColor || '#00C292', 
                        wickDownColor: styles.wickDownColor || styles.downColor || '#E46A76',
                        priceScaleId: priceScaleId 
                    });
                    // Register series for Attached Mode lookup
                    if (indicatorSeriesMap.current) indicatorSeriesMap.current.set(series, layer.id);
                    if (indicatorIdToSeriesMap.current) indicatorIdToSeriesMap.current.set(layer.id, [series]);
                }

                if (priceScaleId === 'left') {
                    chart.applyOptions({
                        leftPriceScale: { visible: true, borderColor: isDarkMode ? '#333333' : '#E6E6E6' },
                        rightPriceScale: { visible: true, borderColor: isDarkMode ? '#333333' : '#E6E6E6' }
                    });
                }
                
                seriesRefs.current.push({ instance: series, type: layer.type, id: layer.id });
            });
            
            //--> Type-prioritized main series selection (Option-A streamlining):
            //*  prefer candle/heikin → line → area → first non-volume → first.
            //*  Previously took the first non-volume series in array order, which
            //*  for a multi-visual-layer strategy could have made the area series
            //*  the "main" — affecting trade-marker anchoring and crosshair sync.
            const mainPriority = ['candle', 'heikin', 'line', 'area'];
            const pickMain = (refs) => {
                for (const t of mainPriority) {
                    const found = refs.find(s => s.type === t);
                    if (found) return found.instance;
                }
                return refs.find(s => s.type !== 'volume')?.instance || refs[0]?.instance;
            };
            mainSeriesRef.current = pickMain(seriesRefs.current);
            //! 50-13 (F-p2-1) the trade plugin is bound to the OLD series; drop it so the next relayout recreates it.
            try { tradeMarkersPluginRef.current?.detach?.(); } catch (e) { /* old series already gone */ } // F-CHARTTYPE-PLUGIN
            tradeMarkersPluginRef.current = null;
            scheduleMarkerRelayoutRef.current?.();
            
            // Check Volume
            const hasExplicitVolumeLayer = layers && layers.some(l => l.type === 'volume');
            const isDefaultMode = !layers || layers.length === 0;
            const shouldShowVolume = hasExplicitVolumeLayer || isDefaultMode;

            if (shouldShowVolume) {
                const volumeLayer = (layers || []).find(l => l.type === 'volume');
                const volStyles = volumeLayer?.styleOptions || {};
                const volUpColor = volStyles.upColor || 'rgba(0, 194, 146, 0.5)';
                const opacity = volStyles.volumeOpacity !== undefined ? volStyles.volumeOpacity : 50;
                const labelPos = volStyles.labelPosition || 'Right';
                
                let volPriceScaleId = labelPos === 'Left' ? 'left' : 'volume';
                const initialVolColor = hexToRgba(volUpColor, opacity);

                const volumeSeries = chart.addSeries(HistogramSeries, {
                    priceFormat: { type: 'volume' },
                    priceScaleId: volPriceScaleId,
                    color: initialVolColor, 
                });
                volumeSeriesRef.current = volumeSeries;
                
                chart.priceScale(volPriceScaleId).applyOptions({
                    scaleMargins: { top: 0.8, bottom: 0 },
                    visible: labelPos !== 'None',
                });
            }

            //! 50-13 R3 — the ONE crosshair callback also drives the marker hover tip (runs even without onCrosshairMove).
            {
                chart.subscribeCrosshairMove((param) => {
                    const tip = markerTipRef.current;
                    if (tip) {
                        const hoverList = (param && param.logical != null && param.point) ? markerHoverRef.current[param.logical] : null;
                        const hoverKey = hoverList ? param.logical : null;
                        //* commit-on-change: DOM writes only when the hovered bar key changes (no React state on mouse move)
                        if (hoverKey !== markerHoverKeyRef.current) {
                            markerHoverKeyRef.current = hoverKey;
                            if (hoverList) {
                                tip.textContent = hoverList.join('\n');
                                tip.style.display = 'block';
                                //* F-TIP-CLAMP: never overflow the container's right edge; flip to the left of the cursor
                                const cw = tip.parentElement?.clientWidth || 0;
                                const tw = tip.offsetWidth || 0;
                                let left = param.point.x + 12;
                                if (cw && left + tw > cw - 4) left = param.point.x - tw - 12;
                                tip.style.left = `${Math.max(4, left)}px`;
                            } else {
                                tip.style.display = 'none';
                            }
                        }
                    }
                    if (!onCrosshairMove) return;
                    const enriched = resolveEnrichedPayload(param, id);
                    // setInternalCrosshair removed for performance. TrendSlopeOverlay subscribes directly to syncService.
                    if (enriched && onCrosshairMove) {
                        if (syncService) syncService.emit(enriched);
                        onCrosshairMove(enriched);
                        //--> Trade level lookup for level visualization
                        if (tradeSpanMapRef.current.length > 0) {
                            const result = findTradeAtTime(tradeSpanMapRef.current, enriched.time);
                            //* Perf (Deepak 10-24): a NEW context object per mouse event re-rendered this whole
                            //* component on every move over a trade; commit only when the hovered trade changes.
                            const nextTrade = result ? result.trade : null;
                            if (nextTrade !== activeTradeRef.current) {
                                activeTradeRef.current = nextTrade;
                                setActiveTradeContext(result ? {
                                    ...result.context,
                                    _levelHistory: result.trade.level_history,
                                    _trade: result.trade, // hover band needs exit_time/fill_bar_start (board #16)
                                } : null);
                            }
                        }
                    } else if (!enriched && onCrosshairMove) {
                        onCrosshairMove(null);
                        if (syncService) syncService.emit(null);
                        if (activeTradeRef.current !== null) { activeTradeRef.current = null; setActiveTradeContext(null); }
                    }
                });
            }
            
        } catch (err) {
            console.error("[SmartChart] Initialization Error:", err);
            throw err; 
        }
    //! Re-init on a SERIES-STRUCTURE change. With no layers (a graph with no visual node — e.g. the CRUDEOIL
    //! WMA40 paper graphs) the series come from the default `[{ type: chartType }]` fallback, so the picked
    //! chart type IS the structure; keying on the layer types alone left the first CandlestickSeries in place
    //! and Line/Area never drew (Heikin only looked right because it reuses the candlestick series).
    }, [id, ((layers && layers.length > 0) ? layers : [{ type: chartType }]).map(l => l.type).join(','), formatChartTimeInExchangeTz, formatTickMark]); // Re-init structure changes

    // --> Scroll Detection for Lazy OHLC Loading
    // Detects when user scrolls to chart edges and triggers page load callbacks.
    // IMPORTANT: Uses refs for paginationState/loadNextPage/loadPreviousPage
    //   so this effect only re-subscribes when `data` changes (new OHLC loaded),
    //   NOT when pagination metadata alone changes (which caused re-trigger loops).
    useEffect(() => {
        if (!chartInstanceRef.current || !data || data.length === 0) {
            return;
        }

        console.log(`[PHASE1-PAGINATION] Scroll detection enabled. Data range: ${data[0].time} to ${data[data.length - 1].time}`);

        // Use a throttle instead of a trailing debounce so it fires DURING the scroll, not after they stop.
        let lastCheckTime = 0;
        let initialCheckFrame = null;
        //* initial=true: the one-shot check after subscribing (below) — acts only on an EMPTY left, not the lead
        const checkEdges = (initial) => {
            try {
                const ps = paginationStateRef.current;
                if (ps?.isLoadingPage) return; // Don't trigger while a page is loading

                const logicalRange = chartInstanceRef.current?.timeScale()?.getVisibleLogicalRange?.();
                if (!logicalRange || !data || data.length === 0) return;

                // Load generously before reaching the edge so the user never sees it
                const THRESHOLD_BARS = 300; 

                // Trigger load previous if scrolled to left edge
                if (logicalRange.from < (initial ? 0 : THRESHOLD_BARS) && ps?.hasPrev && loadPreviousPageRef.current) {
                    console.log(`[PHASE1-PAGINATION] Scroll LEFT EDGE (logical from: ${Math.round(logicalRange.from)}) - loading previous page`);
                    setPaginationLoadDirection('left');
                    loadPreviousPageRef.current();
                }

                // Trigger load next if scrolled to right edge
                if (!initial && logicalRange.to > data.length - THRESHOLD_BARS && ps?.hasNext && loadNextPageRef.current) {
                    console.log(`[PHASE1-PAGINATION] Scroll RIGHT EDGE (logical to: ${Math.round(logicalRange.to)} / ${data.length}) - loading next page`);
                    setPaginationLoadDirection('right');
                    loadNextPageRef.current();
                }
            } catch (err) {
                console.warn("[PHASE1-PAGINATION] Scroll detection error:", err);
            }
        };
        const handleScroll = () => {
            const now = Date.now();
            if (now - lastCheckTime < 150) return; // 150ms throttle
            lastCheckTime = now;
            checkEdges(false);
        };

        try {
            chartInstanceRef.current.timeScale().subscribeVisibleLogicalRangeChange(handleScroll);
            console.log(`[PHASE1-PAGINATION] Subscribed to visible logical range changes`);
            //* LWC 5.1 fires a load's range event synchronously INSIDE series.setData — a commit before this
            //* [data]-keyed subscription exists — so evaluate the CURRENT range once, after the presentation
            //* frame: a live/paper last page narrower than the canvas (fixed barSpacing, scrollToRealTime)
            //* otherwise stayed half-empty until the user panned. Backtest/rd keep their fit/zoom view path.
            if (mode === 'live' || mode === 'paper') {
                initialCheckFrame = requestAnimationFrame(() => { initialCheckFrame = null; checkEdges(true); });
            }
        } catch (e) {
            console.error(`[PHASE1-PAGINATION] Failed to subscribe to scroll:`, e);
        }

        return () => {
            if (initialCheckFrame !== null) cancelAnimationFrame(initialCheckFrame);
            try {
                chartInstanceRef.current?.timeScale()?.unsubscribeVisibleLogicalRangeChange(handleScroll);
                console.log(`[PHASE1-PAGINATION] Unsubscribed from visible logical range changes`);
            } catch (e) {}
        };
    }, [data, mode]); // Only re-subscribe when actual chart data changes

    // Keep time axis and crosshair labels aligned to exchange timezone when instrument changes.
    useEffect(() => {
        if (!chartInstanceRef.current) return;
        chartInstanceRef.current.applyOptions({
            timeScale: {
                tickMarkFormatter: (time, tickMarkType) => formatTickMark(time, tickMarkType),
            },
            localization: {
                timeFormatter: (time) => formatChartTimeInExchangeTz(time),
            },
        });
    }, [formatChartTimeInExchangeTz, formatTickMark]);

    // Cleanup
    useEffect(() => {
        return () => {
            if (chartInstanceRef.current) {
                chartInstanceRef.current.remove();
                chartInstanceRef.current = null;
            }
            tradeMarkersPluginRef.current = null;
        };
    }, []);

    // Theme Updates
    useEffect(() => {
        if (!chartInstanceRef.current) return;
        const themeOptions = {
            layout: {
                backgroundColor: isDarkMode ? '#1a1a1a' : '#FFFFFF',
                textColor: isDarkMode ? '#a3a3a3' : '#3c4350',
            },
            grid: {
                vertLines: { color: isDarkMode ? '#2a2a2a' : 'rgba(0, 0, 0, 0.05)' },
                horzLines: { color: isDarkMode ? '#2a2a2a' : 'rgba(0, 0, 0, 0.05)' },
            },
            timeScale: { borderColor: isDarkMode ? '#333333' : '#E6E6E6' },
            rightPriceScale: { borderColor: isDarkMode ? '#333333' : '#E6E6E6' },
        };
        chartInstanceRef.current.applyOptions(themeOptions);
    }, [isDarkMode]);

    // Style Updates
    useEffect(() => {
        if (!chartInstanceRef.current || !seriesRefs.current) return;
        const activeLayers = (layers && layers.length > 0) ? layers : [{ type: chartType, styleOptions: styleOptions }];
        seriesRefs.current.forEach(seriesObj => {
            const layer = activeLayers.find(l => l.id === seriesObj.id);
            if (layer && layer.styleOptions) {
                const options = {
                    upColor: layer.styleOptions.upColor,
                    downColor: layer.styleOptions.downColor,
                    wickUpColor: layer.styleOptions.wickUpColor,
                    wickDownColor: layer.styleOptions.wickDownColor,
                    borderUpColor: layer.styleOptions.borderUpColor,
                    borderDownColor: layer.styleOptions.borderDownColor,
                    color: layer.styleOptions.color,
                    lineColor: layer.styleOptions.lineColor,
                    topColor: layer.styleOptions.topColor,
                    bottomColor: layer.styleOptions.bottomColor,
                    lineWidth: layer.styleOptions.lineWidth,
                    visible: layer.styleOptions.visible !== false, // Use prop for visibility
                    priceScaleId: layer.styleOptions.labelPosition === 'left' ? 'left' : 'right'
                };
                if (layer.type === 'line') {
                    options.lineStyle = layer.styleOptions.lineStyle;
                    options.lineType = layer.styleOptions.lineType;
                }
                if (layer.type === 'area') options.lineStyle = layer.styleOptions.lineStyle;
                seriesObj.instance.applyOptions(options);

                const isLeft = layer.styleOptions.labelPosition === 'left';
                chartInstanceRef.current.applyOptions({
                    leftPriceScale: { visible: isLeft },
                    rightPriceScale: { visible: !isLeft }
                });
            }
        });
    }, [memoizedLayers, chartType, memoizedStyleOptions]);

    // External Crosshair
    useEffect(() => {
        try {
            if (externalCrosshair && chartInstanceRef.current && externalCrosshair.sourceId !== id) {
                chartInstanceRef.current.setCrosshairPosition(0, externalCrosshair.time, mainSeriesRef.current);
            }
        } catch (e) {  }
    }, [externalCrosshair, id]);

    // --> Sync: Internal Crosshair (from Service) to Main Chart
    useEffect(() => {
        if (!syncService) return;
        
        // Subscribe to Crosshair Events from Children (or other sources)
        const unsubscribe = syncService.subscribe((param) => {
             if (param && chartInstanceRef.current && param.sourceId !== id) {
                 try {
                     chartInstanceRef.current.setCrosshairPosition(0, param.time, mainSeriesRef.current);
                 } catch(e) {}
                 
                 // CRITICAL FIX: Update local crosshair state so overlays (TrendSlope) react to sub-chart hovers
                 // setInternalCrosshair(param); // Removed: Direct Custom Subscription used instead.
                 
                 // Notify StrategyBuilder if needed (Pop-out support)
                 if (onCrosshairMove) onCrosshairMove(param);
             } else if (!param && chartInstanceRef.current) {
                 chartInstanceRef.current.clearCrosshairPosition(); 
                 // setInternalCrosshair(null); // Removed
             }
        });

        return () => unsubscribe();
    }, [syncService, id]);

   
    // ----------------------------------------------------
    // DATA PRESENTATION & TRANSFORMATION
    // ----------------------------------------------------

    // --> Optimization: Create Data-Specific Signature
    // We only want to trigger expensive setData calls when the fundamental data definition changes
    // (e.g. changing from Close to Open, or Heikin to Candle), NOT when just changing color.
    const layerDataSignature = useMemo(() => {
        if (!layers) return '';
        return layers.map(l => {
            // For line/area, the 'field' determines the data. For others, it's just type/id.
            const field = l.styleOptions?.field || '';
            return `${l.id}:${l.type}:${field}`;
        }).sort().join('|');
    }, [memoizedLayers]);

    //--> Track the data source key so we can reset visible range when switching runs.
    const dataSourceKeyRef = useRef('');
    const dateRangeKeyRef = useRef(''); // Tracks dateRange specifically for auto-zooming
    const previousFirstTimeRef = useRef(null);
    const tailKeyRef = useRef('');
    
    //--> Pending zoom: set by auto-zoom when target date isn't in loaded data yet.
    //    The presentation effect checks this after series.setData() and zooms instead
    //    of restoring savedTimeRange.  Cleared after successful zoom.
    const pendingZoomRef = useRef(null); // { from: unixSec, to: unixSec }

    useEffect(() => {
        const isLiveMode = mode === 'live' || mode === 'paper';
        const isHeikin = chartType === 'heikin';
        const seriesKey = `${instrument?.instrument_id}|${timeframe}|${isHeikin}`;
        const previousTailKey = tailKeyRef.current;
        const resetView = previousTailKey !== seriesKey;

        if (rawHistory.length === 0 || !chartInstanceRef.current || seriesRefs.current.length === 0) {
            if (rawHistory.length === 0) {
                setData((previous) => (
                    isLiveMode && previousTailKey === seriesKey
                        ? mergeLiveTail({ historyBars: [], liveBars: previous, isHeikin })
                        : []
                ));
            }
            return;
        }

        // 1. Process Standard Data
        // Prevent duplicate timestamps which crash lightweight-charts
        const uniqueTimes = new Set();
        const standardData = rawHistory.map(d => {
            // --> Parse as UTC so naive exchange-local timestamps display correctly on x-axis
            const utcSec = toUnixSec(d.time ?? d.timestamp);

            return {
            ...d,
            time: utcSec,
            open: extractFieldValue(d, 'OPEN', true),
            high: extractFieldValue(d, 'HIGH', true),
            low: extractFieldValue(d, 'LOW', true),
            close: extractFieldValue(d, 'CLOSE', true),
            value: extractFieldValue(d, 'VOLUME', true),
            };
        })
        .filter(d => {
            if (d.time == null || uniqueTimes.has(d.time)) return false;
            uniqueTimes.add(d.time);
            return true;
        })
        .sort((a,b) => a.time - b.time);

        if (onDataLoaded && standardData.length > 0) {
             onDataLoaded(standardData[standardData.length - 1]);
        }

        console.log(`[PHASE1-PAGINATION] Data presentation: ${standardData.length} records loaded`);
        if (standardData.length > 0) {
            const firstBarTime = new Date(standardData[0].time * 1000).toISOString();
            const lastBarTime = new Date(standardData[standardData.length - 1].time * 1000).toISOString();
            console.log(`[PHASE1-PAGINATION]   Chart displays: ${firstBarTime} to ${lastBarTime}`);
        }

        const activeLayers = (layers && layers.length > 0) ? layers : [{ type: chartType, styleOptions: {} }];

        //--> Capture LOGICAL range before setData() to properly handle negative whitespace panning.
        //    We calculate the exact shift by looking at the index of the previously first bar.
        let savedLogicalRange = null;
        let shiftBarsCount = 0;
        try { 
            savedLogicalRange = chartInstanceRef.current?.timeScale()?.getVisibleLogicalRange(); 
            if (previousFirstTimeRef.current && standardData.length > 0) {
                const shiftIndex = standardData.findIndex(d => d.time === previousFirstTimeRef.current);
                if (shiftIndex > 0) {
                    shiftBarsCount = shiftIndex; // Number of items prepended (on the left)
                }
            }
        } catch (_) {}

        const mergedStandardData = isLiveMode && previousTailKey === seriesKey
            ? mergeLiveTail({ historyBars: standardData, liveBars: dataRef.current, isHeikin: false })
            : standardData;

        // 2. Process Heikin Ashi
        let heikinData = [];
        if (activeLayers.some(l => l.type === 'heikin')) {
            const heikinSource = isHeikin ? standardData : mergedStandardData;
            for (let i = 0; i < heikinSource.length; i++) {
                const d = heikinSource[i];
                const haClose = (d.open + d.high + d.low + d.close) / 4;
                let haOpen;
                if (i === 0) {
                    haOpen = (d.open + d.close) / 2;
                } else {
                    const prev = heikinData[i - 1];
                    haOpen = (prev.open + prev.close) / 2;
                }
                const haHigh = Math.max(d.high, haOpen, haClose);
                const haLow = Math.min(d.low, haOpen, haClose);
                heikinData.push({ ...d, open: haOpen, high: haHigh, low: haLow, close: haClose });
            }
            if (isHeikin && isLiveMode && previousTailKey === seriesKey) {
                heikinData = mergeLiveTail({ historyBars: heikinData, liveBars: dataRef.current, isHeikin: true });
            }
        }

        // We update 'data' state for indicators, but visuals are set on series directly
        const dataToPresent = isHeikin ? heikinData : mergedStandardData;
        setData(dataToPresent);
        // Build Lookup Map for High-Perf Crosshair Sync
        const lookupMap = new Map();
        dataToPresent.forEach(d => lookupMap.set(d.time, d));
        timeToDataMapRef.current = lookupMap;
        syncAnchorTimeRef.current = dataToPresent[0]?.time ?? null; //* range-sync anchor bar (utils.js anchorRange)
        try {
            seriesRefs.current.forEach(seriesObj => {
                const sType = seriesObj.type;
                const sId = seriesObj.id;
                const layerConfig = activeLayers.find(l => l.id === sId) || {};
                const field = layerConfig.styleOptions?.field || 'CLOSE';

                if (sType === 'line' || sType === 'area') {
                     const normalizedField = String(field || '').toUpperCase();
                     const isPriceLikeField =
                         normalizedField.includes('PRICE') ||
                         normalizedField === 'VWAP' ||
                         normalizedField === 'POC_PRICE';

                     const mappedData = dataToPresent.map(d => {
                         const value = extractFieldValue(d, field, true);

                         // For price-like derived fields, treat 0 as invalid/missing to avoid spike-to-zero artifacts.
                         if (isPriceLikeField && value === 0) return null;

                         // Allow indicator zero-crossings for non-price fields.
                         return (value !== undefined && value !== null && !isNaN(value)) ? { time: d.time, value } : null;
                     }).filter(item => item !== null);
                     
                     if (mappedData.length === 0) {
                         // Only warn if we have standardData but mapping results in zero points
                         if (standardData.length > 0) {
                             // console.warn optional: keep strictly for dev mode or remove for cleaner console
                         }
                     } 

                     seriesObj.instance.setData(sanitizeChartData(mappedData));
                } else if (sType === 'heikin') {
                     seriesObj.instance.setData(sanitizeChartData(heikinData));
                } else {
                     seriesObj.instance.setData(sanitizeChartData(dataToPresent));
                }
            });

            if (volumeSeriesRef.current) {
                const dataToUse = activeLayers.some(l => l.type === 'heikin') ? heikinData : dataToPresent;
                const historyLastTime = standardData[standardData.length - 1]?.time;
                const volumeLayer = activeLayers.find(l => l.type === 'volume');
                const volStyles = volumeLayer?.styleOptions || {};
                const volUpColor = volStyles.upColor || 'rgba(0, 194, 146, 0.5)';
                const volDownColor = volStyles.downColor || 'rgba(228, 106, 118, 0.5)';
                const opacity = volStyles.volumeOpacity !== undefined ? volStyles.volumeOpacity : 50;

                volumeSeriesRef.current.setData(sanitizeChartData(dataToUse.map((d) => ({ 
                    time: d.time, 
                    value: d.time > historyLastTime ? barVolume(d) : d.value,
                    color: hexToRgba(d.close >= d.open ? volUpColor : volDownColor, opacity)
                }))));
            }
            
            // Auto-scale
            if (mainSeriesRef.current) {
                if (!isLiveMode || resetView) {
                    chartInstanceRef.current?.priceScale('right').applyOptions({ autoScale: true });
                }
                
                const currentDataSourceKey = JSON.stringify(fetchDateRange || dateRange);
                const isNewDataSource = currentDataSourceKey !== dataSourceKeyRef.current && dataSourceKeyRef.current !== '';

                //--> Detect dateRange drift: the auto-zoom effect keeps dateRangeKeyRef in sync,
                //    so isNewDateRange is only true on the initial load (first rawHistory arrival
                //    when dateRange was set by the auto-zoom effect before data existed).
                const currentDateRangeKey = JSON.stringify(dateRange);
                const isNewDateRange = currentDateRangeKey !== dateRangeKeyRef.current && dateRangeKeyRef.current !== '';

                if (isNewDataSource) {
                    visibleLogicalRangeRef.current = null;
                    if (syncService) syncService.lastRange = null;
                    clearIndicatorData();
                } else if (isNewDateRange) {
                    visibleLogicalRangeRef.current = null;
                }

                if (currentDataSourceKey) dataSourceKeyRef.current = currentDataSourceKey;
                if (currentDateRangeKey) dateRangeKeyRef.current = currentDateRangeKey;

                const storedRange = visibleLogicalRangeRef.current;
                const isRangeValid = storedRange && storedRange.to <= standardData.length && storedRange.from >= -1;

                requestAnimationFrame(() => {
                    if (!chartInstanceRef.current) return;

                    //--> Check for pending zoom FIRST — a period-analysis click set this
                    //    because the target page wasn't loaded yet.  Now that new data has
                    //    arrived via series.setData(), apply the deferred zoom.
                    const pending = pendingZoomRef.current;
                    if (pending) {
                        const firstT = standardData[0]?.time;
                        const lastT = standardData[standardData.length - 1]?.time;
                        const coversNow = firstT && lastT && !(lastT < pending.from || firstT > pending.to);
                        if (coversNow) {
                            console.log(`[PERIOD-ZOOM] Applying pending zoom: ${pending.from}–${pending.to}`);
                            pendingZoomRef.current = null;
                            setLoading(false);
                            setLoadingMessage('');
                            try {
                                animateVisibleRange(chartInstanceRef.current, pending.from, pending.to);
                            } catch (e) { console.warn("[SmartChart] Pending zoom failed:", e); }
                            return; // Don't trigger page cascade
                        }
                        // Data still doesn't cover target — let page loading continue,
                        // keep pending zoom for the next presentation cycle.
                    }

                    //! Live/Paper: always scroll-to-real-time regardless of stored range.
                    //! Must run BEFORE isRangeValid/pagination branches, otherwise the
                    //! subscribeVisibleLogicalRangeChange listener at ~619 captures our
                    //! scroll into visibleLogicalRangeRef and subsequent effect re-runs
                    //! replay it via isRangeValid — pinning the view to the initial fetch
                    //! window while simulated ticks append past the right edge, giving
                    //! the "chart is N days behind" symptom.
                    //! scrollToRealTime uses the library's rightOffset natively and lets
                    //! shiftVisibleRangeOnNewBar (default true) auto-advance as new bars
                    //! arrive via useLiveUpdates' series.update() calls.
                    if (isLiveMode && standardData.length > 0) {
                        if (resetView) chartInstanceRef.current.timeScale().scrollToRealTime();
                    } else if (!isNewDataSource && !isNewDateRange && savedLogicalRange) {
                        //--> Pagination: restore logical range, shifted by prepended items.
                        //    Using logical avoids snapbacks when user has zoomed past left edge.
                        const shiftedLogicalRange = {
                            from: savedLogicalRange.from + shiftBarsCount,
                            to: savedLogicalRange.to + shiftBarsCount
                        };
                        chartInstanceRef.current.timeScale().setVisibleLogicalRange(shiftedLogicalRange);
                        // Removed aggressive proactive loop-loader that triggered when zoomed out.
                    } else if (isRangeValid) {
                        chartInstanceRef.current.timeScale().setVisibleLogicalRange(storedRange);
                    } else if ((mode === 'backtest' || mode === 'rd') && dateRange?.start && dateRange?.end) {
                        //--> Backtest/rd: Force Auto Zoom to the caller's intended window.
                        //    These modes have a fixed, non-advancing data set — we want the
                        //    visible range clamped to dateRange with generous padding.
                        let from = moment.utc(dateRange.start).unix();
                        let to = moment.utc(dateRange.end).unix();

                        // Safety logic: Check if current standardData intersects the required range
                        const hasValidData = standardData.length > 0 && !(standardData[standardData.length-1].time < from || standardData[0].time > to);

                        if (hasValidData) {
                            if (from === to) {
                                from -= 3600;
                                to += 3600;
                            } else {
                                const pad = Math.max(3600, (to - from) * 0.5);
                                from -= pad;
                                to   += pad;
                            }
                            animateVisibleRange(chartInstanceRef.current, from, to);
                        } else {
                            visibleLogicalRangeRef.current = null;
                            dateRangeKeyRef.current = '';
                            chartInstanceRef.current.timeScale().fitContent();
                        }
                    } else {
                        visibleLogicalRangeRef.current = null;
                        chartInstanceRef.current.timeScale().fitContent();
                    }

                    //--> Sync subcharts after data merge / fit
                    //--> Aggressive polling to broadcast master's visible range because layout resolves asynchronously.
                    if (syncService) {
                        let emitAttempts = 0;
                        const emitInterval = setInterval(() => {
                            if (!chartInstanceRef.current || !syncService) {
                                clearInterval(emitInterval); 
                                return;
                            }
                            const seed = chartInstanceRef.current.timeScale().getVisibleLogicalRange();
                            if (seed) {
                                syncService.emitRange(anchorRange(seed, chartInstanceRef.current.timeScale(), syncAnchorTimeRef.current));
                            }
                            emitAttempts++;
                            if (emitAttempts >= 10) clearInterval(emitInterval);
                        }, 100);
                    }
                });

                if (isLiveMode) {
                    console.log('[DIAG] chart view', {
                        action: resetView ? 'reset' : 'kept',
                        reason: resetView ? (previousTailKey ? 'series_change' : 'first_load') : null,
                        priceAutoScale: chartInstanceRef.current?.priceScale('right')?.options?.()?.autoScale,
                        scrollPosition: chartInstanceRef.current?.timeScale()?.scrollPosition?.(),
                    });
                }
            }
        } catch (updateErr) {
            console.error("[SmartChart] Presentation update failed:", updateErr);
        } finally {
            if (standardData && standardData.length > 0) {
                previousFirstTimeRef.current = standardData[0].time;
                tailKeyRef.current = seriesKey;
            }
        }
    //! dateRange intentionally excluded — period-analysis zoom is handled entirely
    //! by the dedicated auto-zoom effect below.  Including dateRange here caused
    //! series.setData() to re-fire with identical data on every period click,
    //! which reset the visible range and wiped out the zoom.
    }, [rawHistory, chartType, layerDataSignature, fetchDateRange]);

    // --> Dedicated Auto Zoom effect for Period Analysis / dateRange changes.
    //    When dateRange changes (e.g. clicking a period row), checks if loaded data
    //    covers the target range. If yes, zooms immediately. If not, calculates the
    //    page that contains the target date and loads it — the presentation effect
    //    retries the zoom when the new data arrives (via dateRangeKeyRef reset).
    useEffect(() => {
        if (!(mode === 'backtest' || mode === 'rd') || !dateRange?.start || !dateRange?.end) return;

        //--> Keep dateRangeKeyRef in sync so the presentation effect (which no longer
        //    depends on dateRange) doesn't falsely detect a "new dateRange" on the next
        //    rawHistory-triggered run and fight the zoom.
        dateRangeKeyRef.current = JSON.stringify(dateRange);

        const from = moment.utc(dateRange.start).unix();
        const to = moment.utc(dateRange.end).unix();

        const d = dataRef.current;
        const firstTime = d?.[0]?.time;
        const lastTime = d?.[d.length - 1]?.time;
        const hasData = d && d.length > 0 && firstTime && lastTime;
        const coversTarget = hasData && !(lastTime < from || firstTime > to);

        //--> Compute the zoom window once (used by both immediate-zoom and pending-zoom paths)
        //--> Symmetric padding centers the trade/period in the viewport.
        //    Pad = max(1h, 50% of duration) so a 30-min trade gets ±1h context,
        //    a 5h trade gets ±2.5h, and a full-day range gets ±12h.
        let zFrom = from;
        let zTo = to;
        if (zFrom === zTo) { zFrom -= 3600; zTo += 3600; }
        else {
            const pad = Math.max(3600, (zTo - zFrom) * 0.5);
            zFrom -= pad;
            zTo   += pad;
        }

        if (coversTarget && chartInstanceRef.current) {
            //--> Data available — zoom immediately and clear any pending zoom
            pendingZoomRef.current = null;

            console.log(`[PERIOD-ZOOM] Zooming to ${dateRange.start} → ${dateRange.end} (unix: ${zFrom}–${zTo})`);
            try {
                animateVisibleRange(chartInstanceRef.current, zFrom, zTo, () => {
                    //--> Sync the final range to any linked panes after animation completes
                    if (!chartInstanceRef.current || !syncService) return;
                    const seed = chartInstanceRef.current.timeScale().getVisibleLogicalRange();
                    if (seed) syncService.emitRange(anchorRange(seed, chartInstanceRef.current.timeScale(), syncAnchorTimeRef.current));
                });
            } catch (e) { console.warn("[SmartChart] Auto zoom failed:", e); }
        } else if (fetchDateRange?.start && paginationStateRef.current?.totalPages) {
            //--> Data not loaded for target range — set pending zoom so the presentation
            //    effect zooms here when the page arrives (instead of restoring savedTimeRange).
            pendingZoomRef.current = { from: zFrom, to: zTo };
            setLoading(true);
            setLoadingMessage("Navigating to date...");
            console.log(`[PERIOD-ZOOM] Set pending zoom: ${zFrom}–${zTo}`);

            //--> Safety net: clear pending zoom + loading after 20s to prevent permanent spinner
            const pendingSnapshot = pendingZoomRef.current;
            setTimeout(() => {
                if (pendingZoomRef.current === pendingSnapshot) {
                    console.warn('[PERIOD-ZOOM] Timeout: clearing stuck pending zoom after 20s');
                    pendingZoomRef.current = null;
                    setLoading(false);
                    setLoadingMessage('');
                }
            }, 20000);

            //--> Calculate and load the right page.
            const ps = paginationStateRef.current;
            const isIntraday = timeframe !== '1D' && timeframe !== '1h';
            const paddingDays = (mode === 'backtest' || fetchDateRange?.start || !isIntraday) ? 180 : 30;
            const paddedStart = moment.utc(fetchDateRange.start).subtract(timeframe === '1D' ? 365 : paddingDays, 'days');
            const rangeEnd = moment.utc(fetchDateRange.end);
            const totalDays = rangeEnd.diff(paddedStart, 'days');
            const pageSize = ps.pageSize || 10;

            const targetMid = moment.utc(dateRange.start);
            const dayOffset = targetMid.diff(paddedStart, 'days');
            const targetPage = Math.max(1, Math.min(
                Math.floor(dayOffset / pageSize) + 1,
                ps.totalPages
            ));

            console.log(`[PERIOD-JUMP] Target date ${dateRange.start} → page ${targetPage}/${ps.totalPages} (dayOffset=${dayOffset}, totalDays=${totalDays})`);
            if (loadPageRef.current) {
                loadPageRef.current(targetPage).then(() => {
                    //--> If the page was already cached, rawHistory didn't change, so the
                    //    presentation effect won't re-fire.  Check if data already covers
                    //    the target and apply the pending zoom directly.
                    if (!pendingZoomRef.current) return; // Already cleared (shouldn't happen, but guard)
                    const d2 = dataRef.current;
                    const ft = d2?.[0]?.time;
                    const lt = d2?.[d2.length - 1]?.time;
                    if (ft && lt && !(lt < pendingZoomRef.current.from || ft > pendingZoomRef.current.to)) {
                        console.log(`[PERIOD-ZOOM] Page was cached — applying zoom directly`);
                        const zoom = pendingZoomRef.current;
                        pendingZoomRef.current = null;
                        setLoading(false);
                        setLoadingMessage('');
                        if (chartInstanceRef.current) {
                            try { animateVisibleRange(chartInstanceRef.current, zoom.from, zoom.to); }
                            catch (e) { console.warn("[SmartChart] Direct zoom failed:", e); }
                        }
                    }
                });
            }
        }
    }, [dateRange, mode, fetchDateRange, timeframe]);

    // ----------------------------------------------------
    // HOOK: Live Updates (WebSocket)
    // ----------------------------------------------------
    // Import from useLiveUpdates logic
    useLiveUpdates({
        mode: replayMode ? 'backtest' : mode,
        liveTick,
        liveNodeResults,
        liveBarTimeframe,
        timeframe, // Passed to hook for alignment
        dataRef,
        setData,
        seriesRefs,
        indicatorIdToSeriesMap,
        volumeSeriesRef,
        chartType,
        layers,
        //--> Push each tick-updated bar to parent so the Core Data panel stays live.
        onBarUpdate: onDataLoaded,
        //--> Gate: skip per-tick updates on graph-node indicator series in live/paper mode.
        //*   indicatorMaps is the authoritative write path for these nodes.
        graphNodeIds,
    });

  const handleManualDownload = async () => {
      setLoading(true); setLoadingMessage("Downloading from Broker...");
      try {
         let startDate, endDate;
         if (mode === 'backtest' && dateRange?.start) {
            startDate = moment.utc(dateRange.start);
            endDate = dateRange.end ? moment.utc(dateRange.end) : moment.utc(dateRange.start).endOf('day');
         } else { endDate = moment(); startDate = moment().subtract(timeframe === '1D' ? 30 : 5, 'days'); }
         await services.marketData.requestHistorical({
             instrument_id: instrument.instrument_id,
             exchange: instrument.exchange,
             symbol: instrument.symbol,
             start_date: startDate.format('YYYY-MM-DD'),
             end_date: endDate.format('YYYY-MM-DD'),
             interval: timeframe
         });
         await new Promise(r => setTimeout(r, 2000));
         fetchHistory();
      } catch (err) {
         setError(err.message || 'Download Request Failed');
         setLoading(false);
      }
  };
  
  //--> Normalize lastEvalTime to epoch seconds (accepts ms, seconds, or ISO string).
  const lastEvalTimeSec = useMemo(() => {
      if (lastEvalTime == null) return null;
      if (typeof lastEvalTime === 'number') return lastEvalTime > 1e12 ? Math.floor(lastEvalTime / 1000) : Math.floor(lastEvalTime);
      const ms = new Date(lastEvalTime).getTime();
      return Number.isFinite(ms) ? Math.floor(ms / 1000) : null;
  }, [lastEvalTime]);

  const {
      indicators,
      activeIndicators,
      removeIndicator,
      updateIndicatorSettings,
      indicatorMaps,
      calculateIndicators,
      clearIndicatorData,
      isCalculating,
      calculateError
  } = useIndicators(
      data,
      memoizedExternalIndicators,
      instrument,
      timeframe,
      memoizedNodes,
      memoizedEdges,
      onError,
      loading,
      {
          mode: replayMode,
          //--> chartMode carries the user-facing mode (live/paper/backtest/rd) so
          //*   useIndicators can gate off REST /calculate when historic + live WS
          //*   streams populate indicatorMaps directly (live/paper deployments).
          chartMode: mode,
          runId: replayRunId,
          onBacktestRequired,
          rolloverDays,
          instruments,
          general,
          expectedStartDate: (fetchDateRange || dateRange)?.start,
          expectedEndDate: (fetchDateRange || dateRange)?.end,
          //--> live inputs; null in backtest mode (no-op)
          liveNodeResults,
          liveEvalTimeSec: lastEvalTimeSec,
          //--> WS message's primary TF — used to gate live-merge against
          //*  chart_tf to prevent V-shape artifacts when deployment_tf != chart_tf.
          liveBarTimeframe,
          //--> Pagination loading flag. useIndicators uses this only during the
          //*  mount window (no prior success) to defer the calculate-effect until
          //*  the auto-edge-trigger merges its second page; afterwards user pan-left
          //*  flows through to the existing delta-left REST path.
          isLoadingPage: paginationState?.isLoadingPage || false,
      }
  );

  //--> Clear pagination edge indicator only when BOTH OHLC load AND indicator recalculation are done
  useEffect(() => {
      if (!paginationState.isLoadingPage && !isCalculating) {
          setPaginationLoadDirection(null);
      }
  }, [paginationState.isLoadingPage, isCalculating]);

  // Notify parent of indicator data updates for dashboard sync
  useEffect(() => {
    if (onIndicatorsUpdate && indicatorMaps) {
        onIndicatorsUpdate(indicatorMaps);
    }
  }, [indicatorMaps, onIndicatorsUpdate]);

  // Sync Refs for Closure Access
  useEffect(() => {
     activeIndicatorsRef.current = activeIndicators;
     calculateIndicatorsRef.current = calculateIndicators;
  }, [activeIndicators, calculateIndicators]);

  //--> Overlay-specific indicator lookup
  const calculateWithLive = useCallback((indicator, fullData, targetTime = null) => {
    const map = indicatorMaps[indicator.id];
    if (!map || !(map instanceof Map)) return targetTime !== null ? null : [];

    const tfMatch = (timeframe || '1m').match(/(\d+)([mhd])/);
    let intSec = 60;
    if (tfMatch) {
      const v = parseInt(tfMatch[1]);
      const u = tfMatch[2];
      if (u === 'm') intSec = v * 60;
      else if (u === 'h') intSec = v * 3600;
      else if (u === 'd') intSec = v * 86400;
    }

    if (targetTime !== null) {
      const val = map.get(targetTime) ?? map.get(targetTime - intSec) ?? map.get(targetTime + intSec);
      if (val !== undefined && val !== null) return val;
      for (let t = targetTime - intSec; t >= targetTime - (24 * 3600); t -= intSec) {
        if (map.has(t)) return map.get(t);
      }
      return null;
    }

    if (fullData && fullData.length > 0) {
      //--> Canonical timestamp coercion via shared helper; floor drift vs prior
      //*   Math.round is ≤1s and cannot shift minute-or-coarser bar keys.
      const getT = (d) => toUnixSec(d.time) ?? toUnixSec(d.timestamp) ?? 0;
      return fullData.map(candle => {
        const t = getT(candle);
        const val = map.get(t) ?? map.get(t - intSec) ?? map.get(t + intSec);
        return val !== undefined && val !== null ? { time: t, value: val } : { time: t };
      });
    }
    return [];
  }, [indicatorMaps, timeframe]);

    //--> In live/paper mode, marker rendering should use merged worker-authoritative
    //    indicator maps (same source used by overlays) to avoid stale marker states.
    const calculateForMarkers = useCallback((indicator, fullData) => {
        if (mode === 'live' || mode === 'paper') {
            return calculateWithLive(indicator, fullData);
        }
        return calculateIndicators(indicator, fullData);
    }, [mode, calculateWithLive, calculateIndicators]);

  // ----------------------------------------------------
  // MEMOIZED OVERLAY DATA (Performance Optimization)
  // ----------------------------------------------------
  const preparedOverlays = useMemo(() => {
        // Filter out hidden indicators from chart visualization
        // (but they're still calculated for dashboard display)

        //--> Build chart data time boundaries for clipping overlay data.
        //--> Indicator rawData may span a wider time range than the paginated OHLC window,
        //--> causing lightweight-charts to render null values and crash.
        const chartTimeMin = data?.length > 0 ? data[0].time : null;
        const chartTimeMax = data?.length > 0 ? data[data.length - 1].time : null;

        return (activeIndicators || [])
            .filter(i => i.overlay && !i.isHidden)
            .flatMap(ind => {
            let rawData = expandScalarPoints(calculateWithLive(ind, data), ind.styleOptions?.scalarOutputKey); //* 142-37 mixed scalar/dict history
            if (!rawData || rawData.length === 0) return [];

            //--> Clip overlay data to chart OHLC time range to prevent null-value crashes.
            //--> When indicator data spans beyond the paginated OHLC window, lightweight-charts
            //--> tries to render series points without corresponding candle positions.
            if (chartTimeMin !== null && rawData.length > data.length) {
                rawData = rawData.filter(d => d.time >= chartTimeMin && d.time <= chartTimeMax);
            }
            if (!rawData || rawData.length === 0) return [];

            // A. Custom / Specialized
            const visualType = (ind.styleOptions?.type || ind.styleOptions?.visualType || ind.styleOptions?.chartType || ind.visual_settings?.type)?.toLowerCase();
            if (visualType === 'custom' || visualType === 'marker' || ind.type === 'dee_trend_slope') {
                return []; // Skip standard rendering for purely custom/portal-based or marker-based overlays
            }

            // B. Chandelier Exit
            if (ind.type === 'chandelier_exit') {
                const dataMap = new Map(data.map(d => [d.time, d]));
                let dropped = 0;
                let withValue = 0;
                const fusedData = rawData.map(d => {
                    const ohlc = dataMap.get(d.time);
                    if (!ohlc) { dropped++; return null; }
                    if (d.value !== undefined && d.value !== null) withValue++;
                    return {
                        time: ohlc.time,
                        value: d.value,
                        open: ohlc.open,
                        close: ohlc.close
                    };
                }).filter(d => d !== null);
                console.log(`[LIVE:chandelier] rawData=${rawData.length} fused=${fusedData.length} dropped=${dropped} withValue=${withValue} firstTime=${rawData[0]?.time} lastTime=${rawData[rawData.length-1]?.time} chartLastTime=${data[data.length-1]?.time}`);
                
                // Map outputStyles to plugin options
                const styles = ind.styleOptions.outputStyles || {};
                const opacity = styles.long_stop?.opacity ?? (styles.short_stop?.opacity ?? 20);
                const opacityHex = Math.round(opacity * 2.55).toString(16).padStart(2, '0');
                
                const upLineColor = styles.long_stop?.color || '#00E676';
                const downLineColor = styles.short_stop?.color || '#FF1744';
                
                const chandelierOptions = {
                    ...ind.styleOptions,
                    upLineColor,
                    downLineColor,
                    upColor: `${upLineColor}${opacityHex}`,
                    downColor: `${downLineColor}${opacityHex}`,
                    lineWidth: styles.long_stop?.lineWidth || ind.styleOptions.lineWidth || 2,
                    lineStyle: styles.long_stop?.lineStyle ?? ind.styleOptions.lineStyle ?? 0,
                    lineType: styles.long_stop?.lineType ?? ind.styleOptions.lineType ?? 0,
                };
                
                return [{
                    type: 'chandelier_custom',
                    id: ind.id,
                    data: fusedData,
                    options: chandelierOptions
                }];
            }

             // B. Multi-value (e.g. Bollinger, Supertrend)
             const sampleVal = rawData.find(d => d?.value && typeof d.value === 'object')?.value ?? rawData[0].value;
             if (typeof sampleVal === 'object' && sampleVal !== null) {
                 return getDrawnOutputKeys(Object.keys(sampleVal)).map(key => {
                     const userOutputStyle = ind.styleOptions.outputStyles?.[key];
                     const finalOptions = {
                         ...ind.styleOptions,
                         ...(userOutputStyle || {})
                     };
                     
                     return {
                        type: 'multi_standard',
                        id: `${ind.id}_${key}`,
                        name: `${ind.type} (${ind.styleOptions.outputLabels?.[key] || key})`,
                        data: rawData.map(d => (d.value ? { time: d.time, value: d.value[key] } : { time: d.time })),
                        options: finalOptions
                     };
                 });
             }

             // C. Scalar Standard
             return [{
                 type: 'standard',
                 id: ind.id,
                 name: ind.type,
                 data: rawData,
                 options: ind.styleOptions
             }];
        });
    }, [activeIndicators, data, calculateWithLive]);

    //--> 50-13 Marker label layout: ONE relayout over BOTH marker sources (trade plugin + attached/angle plugins).
    //*   Glyphs and text stay on createSeriesMarkers; only `text` is rewritten by layoutMarkerLabels.
    const relayoutMarkers = useCallback(() => {
        const chart = chartInstanceRef.current;
        const mainSeries = mainSeriesRef.current;
        if (!chart || !mainSeries) return; // D22 F-p2-5: nothing is published before the chart exists
        try {
            const ts = chart.timeScale();
            let paneHeight;
            //* QA2-ARCH-3: keep the last known pane height when paneSize throws / has no height
            try { paneHeight = chart.paneSize(0)?.height; } catch (e) { paneHeight = undefined; }
            if (Number.isFinite(paneHeight)) markerPaneHeightRef.current = paneHeight; else paneHeight = markerPaneHeightRef.current;
            let paneWidth;
            try { paneWidth = chart.paneSize(0)?.width; } catch (e) { paneWidth = undefined; }
            if (!markerMeasureRef.current) {
                let ctx = null;
                if (typeof document !== 'undefined' && !/jsdom/i.test(navigator.userAgent || '')) {
                    try { ctx = document.createElement('canvas').getContext('2d'); } catch (e) { ctx = null; }
                }
                markerMeasureRef.current = makeTextMeasurer(ctx, MARKER_FONT);
            }
            //* Level rows: only the right-edge in-pane title can touch a marker label; empty title = axis label only (skipped).
            let levelRows = [];
            try {
                levelRows = (mainSeries.priceLines?.() || []).map(l => l.options()).filter(o => o.axisLabelVisible && o.title)
                    .map(o => ({ y: mainSeries.priceToCoordinate(o.price), titleWidth: markerMeasureRef.current(o.title) + LEVEL_TITLE_PAD_PX }))
                    .filter(r => r.y != null);
            } catch (e) { levelRows = []; }
            //--> Gather: (source, series, built markers) with logical index + anchorY on a copy
            const sources = [{ key: 'trade', series: mainSeries, plugin: tradeMarkersPluginRef.current, built: tradeBuiltMarkersRef.current || [], kind: null }];
            attachedMarkersRef.current.forEach((built, series) => {
                sources.push({ key: `att${sources.length}`, series, plugin: series._markersPlugin, built, kind: 'angle' });
            });
            const logicalCache = new Map();
            const logicalOf = (t) => {
                if (!logicalCache.has(t)) { const ix = ts.timeToIndex(t, true); logicalCache.set(t, ix); }
                return logicalCache.get(t);
            };
            const combined = [];
            sources.forEach(src => {
                src.built.forEach((m, n) => {
                    const logical = logicalOf(m.time);
                    let anchorY = null;
                    //* F-CROSS-COORD: levelRows are MAIN-series coordinates; markers on other series get no anchorY (skip the yield)
                    if (logical != null && src.series === mainSeries) {
                        try {
                            const d = src.series.dataByIndex(logical, 0);
                            const price = !d ? null : m.position === 'aboveBar' ? (d.high ?? d.value ?? d.close)
                                : m.position === 'belowBar' ? (d.low ?? d.value ?? d.close) : (d.value ?? d.close);
                            if (price != null) anchorY = src.series.priceToCoordinate(price);
                        } catch (e) { anchorY = null; }
                    }
                    combined.push({ ...m, id: `${src.key}:${n}`, logical, anchorY, ...(src.kind ? { kind: src.kind } : {}), _src: src, _n: n });
                });
            });
            const { markers: laid, hoverByIndex } = layoutMarkerLabels(combined, {
                timeToX: (t) => { const ix = logicalOf(t); return ix == null || typeof ts.logicalToCoordinate !== 'function' ? null : ts.logicalToCoordinate(ix); },
                barSpacing: ts.options?.().barSpacing,
                paneHeight,
                paneWidth,
                levelRows,
                measureText: markerMeasureRef.current,
            });
            //--> Slice back per source, strip layout-only fields, apply only when the signature changed
            const slices = new Map(sources.map(src => [src, []]));
            laid.forEach(m => {
                //! R4: the library marker is the BUILT marker with ONLY `text` replaced (never colour/shape/position/time)
                slices.get(m._src)[m._n] = { ...m._src.built[m._n], text: m.text };
            });
            let changed = false;
            sources.forEach(src => {
                const out = slices.get(src);
                const sig = out.map(m => `${m.time}|${m.position}|${m.shape}|${m.color}|${m.size}|${m.text}`).join('\n');
                let plugin = src.key === 'trade' ? tradeMarkersPluginRef.current : src.series._markersPlugin;
                if (!plugin) {
                    if (out.length === 0) return;
                    plugin = createSeriesMarkers(src.series, out);
                    if (src.key === 'trade') tradeMarkersPluginRef.current = plugin; else src.series._markersPlugin = plugin;
                    markerSigByPlugin.set(plugin, sig); changed = true;
                } else if (markerSigByPlugin.get(plugin) !== sig) {
                    plugin.setMarkers(out);
                    markerSigByPlugin.set(plugin, sig); changed = true;
                }
            });
            markerHoverRef.current = hoverByIndex;
            if (changed) {
                markerHoverKeyRef.current = null;
                if (markerTipRef.current) markerTipRef.current.style.display = 'none';
            }
        } catch (e) {
            console.error('[SmartChart] Error laying out markers:', e);
        }
    }, []);
    //* One rAF per frame (ChartSyncService.emitRange pattern); the crosshair callback never calls this.
    const scheduleMarkerRelayout = useCallback(() => {
        if (markerFrameRef.current != null) return;
        markerFrameRef.current = requestAnimationFrame(() => {
            markerFrameRef.current = null;
            relayoutMarkers();
        });
    }, [relayoutMarkers]);
    scheduleMarkerRelayoutRef.current = scheduleMarkerRelayout;
    //* A hovered-trade change moves the level rows: one relayout (coalesced into the same frame).
    useEffect(() => { scheduleMarkerRelayout(); }, [activeTradeContext, scheduleMarkerRelayout]);
    useEffect(() => () => {
        if (markerFrameRef.current != null) { cancelAnimationFrame(markerFrameRef.current); markerFrameRef.current = null; }
    }, []);

    //--> Trade Execution Markers: separate plugin instance from AttachedModeManager
    useEffect(() => {
        const mainSeries = mainSeriesRef.current;
        if (!mainSeries || !tradeMarkers || tradeMarkers.length === 0) {
            //# Clear existing trade markers if any (the relayout empties the plugin)
            tradeBuiltMarkersRef.current = [];
            scheduleMarkerRelayout();
            return;
        }

        //--> Sort markers by time (required by lightweight-charts)
        const sorted = [...tradeMarkers].sort((a, b) => {
            const timeA = typeof a.time === 'string' ? new Date(a.time).getTime() : a.time;
            const timeB = typeof b.time === 'string' ? new Date(b.time).getTime() : b.time;
            return timeA - timeB;
        });

        //--> Filter markers to only those within the loaded candle data range.
        //    With paginated OHLC loading, only the last page loads initially.
        //    Markers outside the loaded range have no time-scale context and would
        //    collapse to the left edge.  As pagination loads more data, this effect
        //    re-fires (data is a dependency) and more markers become visible.
        let markersToSet = sorted;
        if (data && data.length > 0) {
            const getUnix = (t) => {
                if (typeof t === 'number') return t > 1e10 ? Math.floor(t / 1000) : Math.floor(t);
                if (typeof t === 'string') return Math.floor(new Date(t).getTime() / 1000);
                if (t && typeof t === 'object' && t.year) {
                    return Math.floor(Date.UTC(t.year, (t.month || 1) - 1, t.day || 1) / 1000);
                }
                return 0;
            };
            const firstDataTime = getUnix(data[0].time);
            const lastDataTime = getUnix(data[data.length - 1].time);
            markersToSet = sorted.filter(m => {
                const markTime = getUnix(m.time);
                // Give right bound 1 week leeway so live intra-candle unaligned timestamps pass
                    return markTime >= firstDataTime && markTime <= lastDataTime + (86400 * 7);
            });
        } else {
            //# No candle data loaded yet — skip setting markers entirely
            markersToSet = [];
        }

        if (markersToSet.length === 0) {
            //# Clear stale markers if data range shifted and no markers match
            tradeBuiltMarkersRef.current = [];
            scheduleMarkerRelayout();
            return;
        }

        //--> 50-13: hand the built markers to the ONE relayout (it creates / updates the plugin via the layout helper)
        tradeBuiltMarkersRef.current = markersToSet;
        scheduleMarkerRelayout();
    }, [tradeMarkers, data, scheduleMarkerRelayout]);

  //--> 50-11: the main chart always owns the time axis (panes never draw one). Collapsed card: at most
  //*   MAX_COLLAPSED_STRIPS rows — beyond that, two strips + one '+M more' row (hidden panes unmount).
  const subPaneIndicators = (activeIndicators || []).filter(isSubPaneIndicator);
  const subPaneCount = subPaneIndicators.length;
  //* 50-12: tabs mode (collapsed card only) keeps EVERY pane mounted in one 44px row — no cap, no '+M more'.
  const isPaneTabs = compactPanes && paneStripMode === 'tabs';
  const visiblePanes = compactPanes && !isPaneTabs && subPaneCount > MAX_COLLAPSED_STRIPS
      ? subPaneIndicators.slice(0, MAX_COLLAPSED_STRIPS - 1)
      : subPaneIndicators;
  const hiddenPaneCount = subPaneCount - visiblePanes.length;

  /*>>> TOOLBAR: idle fade <<<*/
  const { isIdle, wake } = useToolbarIdle(false);
  const idleHide = isIdle
      ? `${TOOLBAR_MOTION.exit} opacity-0 -translate-x-1 scale-[0.98] pointer-events-none [&:has(:focus-visible)]:opacity-100 [&:has(:focus-visible)]:translate-x-0 [&:has(:focus-visible)]:scale-100 [&:has(:focus-visible)]:pointer-events-auto`
      : `${TOOLBAR_MOTION.enter} opacity-100 translate-x-0 scale-100`;
  //--> collapsing wrapper: grid 0fr<->1fr closes the gap without measuring (PAPER chip glides left); -mr-2 cancels the flex gap
  const collapseWrap = isIdle
      ? `${TOOLBAR_MOTION.exit} grid-cols-[0fr] -mr-2 [&:has(:focus-visible)]:grid-cols-[1fr] [&:has(:focus-visible)]:mr-0`
      : `${TOOLBAR_MOTION.enter} grid-cols-[1fr] mr-0`;

  return (
    <div 
        ref={mainWrapperRef}
        className={`relative flex flex-col bg-white dark:bg-bg-2 rounded-lg border border-gray-200 dark:border-gray-800 shadow-sm ${heightClass}`}
    >

      
      {/* FLOATING HEADER (Glassmorphism) - Single Line Consolidated */}
      {showToolbar && (
        <div
            className="absolute top-4 left-4 z-20 flex items-center gap-2 pointer-events-none flex-wrap"
            style={{ maxWidth: `calc(100% - ${16 + PRICE_SCALE_WIDTH + 42}px)` }}
            data-testid="smartchart-toolbar"
            data-idle={isIdle ? 'true' : 'false'}
            onPointerEnter={wake}
            onPointerMove={wake}
            onTouchStart={wake}
            onFocus={wake}
            onClick={dropPointerFocus}
        >
            
            {/* 1. Instrument Title & Info */}
            <div className="flex items-center gap-2 pointer-events-auto bg-bg-3/70 dark:bg-bg-2/70 backdrop-blur-lg border border-black/5 dark:border-white/10 rounded px-3 py-1.5 shadow-sm h-8">
                <h2 className="text-sm font-black text-gray-900 dark:text-gray-100 tracking-tight flex items-center gap-2 whitespace-nowrap">
                    {instrument.symbol}
                </h2>
                <div className="px-1.5 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-300 text-[9px] font-bold rounded uppercase tracking-wider border border-gray-200 dark:border-gray-600 whitespace-nowrap">
                    {instrument.exchange}
                </div>
                {/* 1b. Timeframe lives in the chip ('SYMBOL EXCH · 5m', ce3827d0b/b4f8cb358 look); the timeframe part expands the options */}
                <span aria-hidden="true" className="text-[10px] font-bold text-gray-500 dark:text-gray-400 -mr-2">·</span>
                <PillGroup
                    label="Timeframe"
                    frameClassName="flex items-center h-full"
                    items={timeframes.map((tf) => ({ id: tf }))}
                    activeId={timeframe}
                    onSelect={handleTimeframeChange}
                    renderPill={(item, p) => (
                        <button key={item.id} type="button" {...p}
                            className={`${p.className} text-[10px] font-bold rounded-sm flex items-center ${
                                p['aria-pressed']
                                    ? (p['aria-expanded'] ? 'text-[var(--color-accent)]' : 'text-gray-500 dark:text-gray-400 hover:text-fg-1 dark:hover:text-fg-1')
                                    : 'text-fg-3 hover:text-fg-1 dark:hover:text-fg-1'
                            } transition-colors`}
                        >
                            {item.id}
                        </button>
                    )}
                />
            </div>

            {/* 2. Chart type pills (only when the host can change the type) — always expanded, idle-collapsing (5ae6240f3) */}
            {onChartTypeChange && (
            <div className={`grid transition-[grid-template-columns,margin] motion-reduce:transition-none ${collapseWrap}`}>
              <div className="flex items-center gap-2 min-w-0 overflow-hidden">
            <div role="group" aria-label="Chart type" className={`flex items-center bg-bg-3/70 dark:bg-bg-2/70 backdrop-blur-lg border border-black/5 dark:border-white/10 rounded p-1 shadow-sm h-8 pointer-events-auto transition-[opacity,transform] motion-reduce:transition-none ${idleHide}`}>
                {[
                    { id: 'candle', icon: FiBarChart2, label: 'Candles' },
                    { id: 'line', icon: FiActivity, label: 'Line' },
                    { id: 'heikin', icon: FiTrendingUp, label: 'Heikin' },
                    { id: 'area', icon: FiLayers, label: 'Area' }
                ].filter(t => !availableTypes || availableTypes.includes(t.id)).map((type) => (
                    <Tooltip key={type.id} content={type.label} placement="bottom">
                        <button
                            type="button"
                            aria-label={type.label}
                            aria-pressed={chartType === type.id}
                            onClick={() => onChartTypeChange(type.id)}
                            className={`px-2 py-1 text-[10px] font-bold rounded-sm h-full flex items-center ${
                                chartType === type.id
                                    ? 'bg-[var(--color-accent)] text-white shadow-sm'
                                    : 'text-fg-3 hover:text-fg-1 dark:hover:text-fg-1 hover:bg-black/5 dark:hover:bg-white/5'
                            } transition-all`}
                        >
                            <type.icon size={14} />
                        </button>
                    </Tooltip>
                ))}
            </div>
              </div>
            </div>
            )}

            {/* 4. Status Indicators (Moved here from top-right) — stays visible while idle */}
             <div className="pointer-events-auto flex items-center gap-2 px-3 py-1.5 bg-bg-3/70 dark:bg-bg-2/70 backdrop-blur-lg border border-black/5 dark:border-white/10 rounded shadow-sm h-8">
                 <div className={`w-2 h-2 rounded-full ${
                    mode === 'live' ? 'bg-green-500 animate-pulse' :
                    mode === 'paper' ? 'bg-blue-500 animate-pulse' :
                    'bg-orange-500'
                 }`}></div>
                 <span className="text-[10px] font-bold text-gray-600 dark:text-gray-300 uppercase tracking-widest whitespace-nowrap">
                    {mode === 'live' ? 'Live' :
                     mode === 'paper' ? 'Paper' :
                     'Backtest'}
                 </span>
                 {typeof tickType === 'string' && tickType.toLowerCase() === 'simulated' && (
                   <span className="text-[9px] text-gray-400 dark:text-gray-500 tracking-wide whitespace-nowrap">
                     · Simulated
                   </span>
                 )}
            </div>
            
        </div>
      )}

      {/* Backtest Range (Centered at Top) */}
      {showToolbar && mode === 'backtest' && dateRange?.start && (
        <div className="absolute top-4 left-1/2 -translate-x-1/2 z-20 pointer-events-none">
            <div className="pointer-events-auto bg-indigo-50/90 dark:bg-indigo-900/80 backdrop-blur border border-indigo-100 dark:border-indigo-800 rounded px-3 py-1.5 shadow-sm h-8 flex items-center gap-2">
                <span className="text-[10px] font-bold text-indigo-400 uppercase tracking-wider">Backtest Range:</span>
                <span className="text-xs font-mono font-bold text-indigo-700 dark:text-indigo-200 whitespace-nowrap">
                    {moment.utc(dateRange.start).tz(exchangeTz).format('D MMM')} - {dateRange.end ? moment.utc(dateRange.end).tz(exchangeTz).format('D MMM') : 'Now'}
                </span>
            </div>
        </div>
      )}

      {/* Chart Canvas Area */}
      {/* 50-11: flex-1 + a pixel floor — the sub-pane stack can never starve the candles */}
      <div
        className="flex-1 relative w-full min-h-0 overflow-hidden"
        style={{ minHeight: MAIN_CANVAS_MIN_PX }}
        data-testid="smartchart-canvas"
        //* F4 (Verify): hovering anywhere over the plot wakes the faded toolbar (the faded groups are
        //*   pointer-events-none, so the toolbar box alone could not see the pointer); wake() is a
        //*   same-state bail-out when already awake, so pointer moves do not re-render the chart.
        onPointerMove={wake}
      >
        {/* 50-13 R3: ONE marker hover tip, written imperatively by the crosshair callback (no React state) */}
        <div
          ref={markerTipRef}
          data-testid="marker-hover-tip"
          style={{ display: 'none', position: 'absolute', top: 8, left: 4, zIndex: 30, pointerEvents: 'none', whiteSpace: 'pre',
            maxWidth: 360, padding: '4px 8px', borderRadius: 4, fontSize: 12, lineHeight: '16px',
            background: 'rgba(17,24,39,0.92)', color: '#F9FAFB', border: '1px solid rgba(255,255,255,0.15)' }}
        />
        {/* Go to Real-Time Button — 142-42: lives INSIDE the main canvas, just left of the price scale
            and above the time axis, so it never covers price/sub-pane scale labels (it used to sit
            bottom-right of the whole card, on top of the last sub-pane's scale numbers). */}
        <button
            onClick={() => chartInstanceRef.current?.timeScale().scrollToRealTime()}
            className="absolute bottom-8 z-20 p-1 bg-white/90 dark:bg-bg-2/90 border border-gray-200 dark:border-gray-700 rounded-full shadow-sm text-gray-500 hover:text-blue-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
            style={{ right: PRICE_SCALE_WIDTH + 6 }}
            title="Scroll to Real-Time"
            aria-label="Scroll to Real-Time"
        >
            <FiChevronsRight className="w-3.5 h-3.5" />
        </button>
        {canvasAction && (
          <div
            data-testid="smartchart-canvas-action"
            className="absolute top-1.5 z-20 opacity-40 hover:opacity-100 focus-within:opacity-100 transition-opacity"
            style={{ right: PRICE_SCALE_WIDTH + 6 }}
          >
            {canvasAction}
          </div>
        )}
        {calculateError && (
          <div role="alert" className="absolute top-1 left-1/2 -translate-x-1/2 z-30 rounded bg-red-950/90 px-2 py-1 text-[10px] text-red-200 shadow">
            {calculateError.status ? `${calculateError.status}: ` : ''}{calculateError.message}
          </div>
        )}
        <ChartErrorBoundary onRetry={fetchHistory} heightClass="h-full">
            {/* Watermark */}
            <div className="absolute bottom-8 left-4 z-[1] pointer-events-none opacity-[0.15] select-none">
                 <img src="/logo-light.png" alt="DeeTrade" className="h-12 md:h-16 w-auto mix-blend-multiply dark:mix-blend-screen" />
            </div>

            {/* Chart Container */}
            <div 
                ref={chartContainerRef} 
                className="w-full h-full relative z-[2]"
                style={{ position: 'relative' }} // Ensure crosshair pos works relative to this
            />
            
            {/* DEBUG: Layer Error Warnings */}
            {(layers || []).map(l => {
                 // Check if series is active but has no data
                 const ref = seriesRefs.current.find(r => r.type === l.type);
                 // We can't access series data directly easily without state, but we can verify if 'field' is valid
                 // This is a UI helper for the user report
                 if (l.styleOptions?.field && !['OPEN','HIGH','LOW','CLOSE','VOLUME'].includes(l.styleOptions.field)) {
                     // Check sample data point
                     const sample = dataRef.current?.[dataRef.current.length - 1];
                     const val = sample ? extractFieldValue(sample, l.styleOptions.field) : undefined;
                     
                     if (sample && val === undefined) {
                         return (
                             <div key={l.id} className="absolute top-2 right-2 bg-red-500/90 text-white text-[10px] p-2 rounded shadow-lg z-50 max-w-[200px]">
                                 <strong>Mapping Error:</strong>
                                 <div className="font-mono mt-1">Field '{l.styleOptions.field}' not found in data stream.</div>
                                 <div className="mt-1 opacity-80 text-[8px]">Keys: {Object.keys(sample).slice(0, 5).join(', ')}...</div>
                             </div>
                         );
                     }
                 }
                 return null;
            })}

            {/* Indicator Overlays (Data Memoized, Rendering Live) */}
            {preparedOverlays.map(item => {
                if (item.type === 'chandelier_custom') {
                    return (
                        <ChandelierOverlay
                            key={item.id}
                            id={item.id}
                            data={item.data}
                            options={item.options}
                            chartInstance={chartInstanceRef.current}
                            onSeriesCreated={handleIndicatorSeriesCreated}
                        />
                    );
                }
                // Handle Standard/Multi (Including multi-value keys)
                return (
                    <IndicatorOverlay 
                        key={item.id} 
                        id={item.id}
                        type={item.name || item.id}
                        data={item.data}
                        color={item.options?.color}
                        lineWidth={item.options?.lineWidth}
                        lineStyle={item.options?.lineStyle}
                        lineType={item.options?.lineType}
                        chartInstance={chartInstanceRef.current}
                        onSeriesCreated={handleIndicatorSeriesCreated}
                    />
                );
            })}

            {/* Loaders & Overlays */}
            {loading && (
                <div className="absolute inset-0 bg-white/80 dark:bg-bg-2/80 flex flex-col items-center justify-center z-10 backdrop-blur-sm">
                    <Loader size="md" />
                    <span className="mt-2 text-xs font-medium text-gray-500 animate-pulse">{loadingMessage}</span>
                </div>
            )}

            {/* --> Subtle edge loading indicator for pagination + indicator recalculation */}
            {paginationLoadDirection && (paginationState.isLoadingPage || isCalculating) && (
                <div className={`absolute top-1/2 -translate-y-1/2 z-10 ${paginationLoadDirection === 'left' ? 'left-2' : 'right-2'}`}>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-gray-900/70 dark:bg-white/10 backdrop-blur-sm shadow-lg animate-fadeIn">
                        <svg className="w-3.5 h-3.5 text-blue-400 animate-spin" viewBox="0 0 24 24" fill="none">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        <span className="text-[10px] font-medium text-white/80 whitespace-nowrap">
                            {paginationState.isLoadingPage ? 'Loading data...' : 'Calculating indicators...'}
                        </span>
                    </div>
                </div>
            )}
            
            {error && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/90 dark:bg-bg-2/90 z-20">
                    <div className="text-center p-6 max-w-sm ml-auto mr-auto">
                        <div className="text-red-500 mb-2 mx-auto w-10">
                             <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                             </svg>
                        </div>
                        <p className="text-sm text-gray-800 dark:text-gray-200 font-medium mb-1">Unable to load data</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400 mb-4">{error}</p>
                        <button 
                            onClick={fetchHistory}
                            className="text-xs px-3 py-1.5 bg-gray-100 hover:bg-gray-200 dark:bg-gray-800 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300 rounded border border-gray-300 dark:border-gray-600 transition-colors"
                        >
                            Try Again
                        </button>
                    </div>
                </div>
            )}

            {/* Premium Indicator Overlays (WOW factors) */}
            <TrendSlopeOverlay 
                activeIndicators={activeIndicators}
                syncService={syncService} // Direct Subscription for Performance
                chartContainerRef={mainWrapperRef} // Portal to root to span all panes
            />

            {/* Trade Level Overlay (SL/TP/trailing lines on hover or always-visible) */}
            {tradeSpanMap.length > 0 && (
                <TradeLevelOverlay
                    series={mainSeriesRef.current}
                    chart={chartInstanceRef.current}
                    chartRef={chartInstanceRef}
                    activeTradeContext={isAlwaysVisible ? null : activeTradeContext}
                    allTrades={isAlwaysVisible ? levelHistory : null}
                    levelDisplayConfigs={levelDisplayConfigs}
                    mode={Object.values(levelDisplayConfigs).find(c => c.enabled)?.mode || 'price_lines'}
                    hoverOnly={!isAlwaysVisible}
                    timeframe={timeframe}
                />
            )}

            {/* Attached Mode Marker Propagation & Styling */}
            <AttachedModeManager
                activeIndicators={activeIndicators}
                calculateIndicators={calculateForMarkers}
                data={data}
                indicatorSeriesMap={indicatorSeriesMap}
                indicatorIdToSeriesMap={indicatorIdToSeriesMap}
                registryVersion={registryVersion}
                edges={edges || []}
                mainSeriesRef={mainSeriesRef}
                attachedMarkersRef={attachedMarkersRef}
                onMarkersBuilt={scheduleMarkerRelayout}
            />
        </ChartErrorBoundary>
      </div>
      
      {/* Render Separate Panes (RSI, MACD, etc.) */}
      {/* 50-11: ONE stack, class switches. Full: takes what the main chart leaves (shrinks, then SCROLLS).
          compactPanes: fixed PANE_STRIP_HEIGHT rows, never scrolls; panes beyond the cap collapse into one
          '+M more' row. Same keyed children in both modes, so toggling never remounts a strip pane. */}
      <div
          className={isPaneTabs
              ? 'no-scrollbar flex flex-row shrink-0 overflow-x-auto overflow-y-hidden border-t border-gray-200 dark:border-gray-800'
              : compactPanes
                  ? 'flex flex-col shrink-0 overflow-hidden border-t border-gray-200 dark:border-gray-800'
                  : 'no-scrollbar flex flex-col min-h-0 overflow-y-auto overflow-x-hidden border-t border-gray-200 dark:border-gray-800'}
          style={isPaneTabs ? { height: PANE_STRIP_HEIGHT } : undefined}
          role={isPaneTabs ? 'group' : undefined} // Verify F7: expand-on-click buttons, not selectable tabs
          aria-label={isPaneTabs ? 'Indicator panes' : undefined}
          data-testid="smartchart-panes"
      >
          {visiblePanes.map((ind) => (
              //* 50-12: ONE stable keyed wrapper per pane in every mode (display: contents outside tabs), so a
              //*   rows <-> tabs switch never remounts a pane chart. In tabs the wrapper is the tab (strip value
              //*   = IndicatorPane's own latest-value chip); the child omits onActivate to avoid nested buttons.
              <div
                  key={ind.id}
                  data-testid={`smartchart-pane-tab-${ind.id}`}
                  style={isPaneTabs
                      ? { display: 'flex', flexDirection: 'column', flex: '1 1 0', minWidth: 132, height: PANE_STRIP_HEIGHT, cursor: onPaneActivate ? 'pointer' : undefined }
                      : { display: 'contents' }}
                  {...(isPaneTabs && {
                      role: 'button',
                      tabIndex: onPaneActivate ? 0 : -1,
                      onClick: onPaneActivate || undefined,
                      onKeyDown: onPaneActivate ? (e) => {
                          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onPaneActivate(); }
                      } : undefined,
                  })}
              >
                  <SyncedIndicatorWrapper
                      ind={ind}
                      compact={compactPanes}
                      onActivate={isPaneTabs ? undefined : onPaneActivate}
                      data={data}
                      calculateIndicators={calculateIndicators}
                      isDarkMode={isDarkMode}
                      onCrosshairMove={onCrosshairMove}
                      onSeriesCreated={handleIndicatorSeriesCreated}
                      resolveEnrichedPayload={resolveEnrichedPayload}
                      syncService={syncService}
                      externalCrosshair={externalCrosshair}
                      tickMarkFormatter={formatTickMark}
                      timeFormatter={formatChartTimeInExchangeTz}
                  />
              </div>
          ))}
          {compactPanes && hiddenPaneCount > 0 && (
              <button
                  type="button"
                  key="more"
                  data-testid="smartchart-panes-more"
                  onClick={onPaneActivate || undefined}
                  className="w-full border-t border-gray-100 dark:border-gray-800 text-[11px] font-mono font-bold text-gray-500 hover:bg-gray-50 dark:hover:bg-gray-800"
                  style={{ flex: `0 0 ${PANE_STRIP_HEIGHT}px`, height: PANE_STRIP_HEIGHT }}
              >
                  +{hiddenPaneCount} more
              </button>
          )}
      </div>
    </div>
  );
};

//--> Compact pill group (the timeframe part of the symbol chip): shows only the selected pill until the pointer enters the group or focus lands in it.
//*   Collapses PILL_COLLAPSE_MS after the pointer leaves (unless focus is inside), on Escape, on blur-out and on select.
//*   Canvas hover never reaches here (only the toolbar's own wake runs), so it cannot expand the lists.
const PILL_COLLAPSE_MS = 400;
const PillGroup = ({ label, items, activeId, onSelect, renderPill, frameClassName }) => {
    const [open, setOpen] = useState(false);
    const timerRef = useRef(null);
    const groupRef = useRef(null);
    const cancel = () => { if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; } };
    useEffect(() => cancel, []);
    const expand = () => { cancel(); setOpen(true); };
    const collapseLater = () => {
        cancel();
        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            if (!groupRef.current?.contains(document.activeElement)) setOpen(false);
        }, PILL_COLLAPSE_MS);
    };
    const handleKeyDown = (e) => {
        if (e.key === 'Escape') { cancel(); setOpen(false); return; }
        if (!open && ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) { e.preventDefault(); expand(); }
    };
    return (
        <div
            ref={groupRef}
            role="group"
            aria-label={label}
            onPointerEnter={expand}
            onPointerLeave={collapseLater}
            onFocus={expand}
            onBlur={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) collapseLater(); }}
            onKeyDown={handleKeyDown}
            className={frameClassName}
        >
            {items.map((item) => {
                const active = item.id === activeId;
                //--> hidden pills stay mounted and glide (max-w/opacity/padding) — out of tab order + a11y tree while collapsed
                const hidden = !open && !active;
                return renderPill(item, {
                    className: `overflow-hidden whitespace-nowrap transition-[max-width,opacity,padding] motion-reduce:transition-none ${
                        hidden
                            ? `${TOOLBAR_MOTION.exit} max-w-0 opacity-0 px-0 pointer-events-none`
                            : `${TOOLBAR_MOTION.enter} max-w-[4rem] opacity-100 px-1`}`,
                    'data-collapsed': hidden ? 'true' : 'false',
                    ...(hidden ? { 'aria-hidden': true, tabIndex: -1 } : {}),
                    'aria-pressed': active,
                    ...(active ? { 'aria-expanded': open } : {}),
                    onClick: () => {
                        //! collapsed + click/Enter/Space on the selected pill only expands (touch + keyboard); otherwise select + collapse
                        if (!open) { expand(); return; }
                        cancel(); setOpen(false); onSelect(item.id);
                    },
                });
            })}
        </div>
    );
};

//--> 50-11: toolbar idle fade. Timer lives in a ref (React state changes only when idle flips).
//*   Reduced-motion users never fade (matchMedia guard copied from ds/haptics.js:45). `hold` keeps it awake.
const TOOLBAR_IDLE_MS = 2500;
//--> Motion guideline: transform/opacity, 150-300 ms, ease-out on enter / ease-in on exit (exit ~65% of enter), reduced-motion = none.
const TOOLBAR_MOTION = {
    exit: 'duration-[160ms] ease-in',
    enter: 'duration-[250ms] ease-out',
};
//! A mouse-clicked pill keeps focus; Chromium turns that focus :focus-visible at the NEXT key press (any key,
//*   Alt/Ctrl included — alt-tab, copy) and the [&:has(:focus-visible)] keyboard override then pins the faded
//*   groups open for good. Drop focus after a pointer click (detail > 0); keyboard activation (detail 0) keeps it.
const dropPointerFocus = (e) => {
    if (e.detail > 0) e.target.closest?.('button')?.blur();
};
const useToolbarIdle = (hold) => {
    const [isIdle, setIsIdle] = useState(false);
    const timerRef = useRef(null);
    const holdRef = useRef(hold);
    holdRef.current = hold;
    const reducedRef = useRef(null);
    const clear = useCallback(() => {
        if (timerRef.current) { clearTimeout(timerRef.current); timerRef.current = null; }
    }, []);
    const arm = useCallback(() => {
        clear();
        if (reducedRef.current === null) {
            try {
                reducedRef.current = typeof window.matchMedia === 'function'
                    && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
            } catch (e) { reducedRef.current = false; }
        }
        if (reducedRef.current) return;
        timerRef.current = setTimeout(() => { timerRef.current = null; setIsIdle(true); }, TOOLBAR_IDLE_MS);
    }, [clear]);
    const wake = useCallback(() => {
        setIsIdle(false);
        if (!holdRef.current) arm();
    }, [arm]);
    useEffect(() => {
        if (hold) { clear(); setIsIdle(false); } else { arm(); }
        return clear;
    }, [hold, arm, clear]);
    return { isIdle, wake };
};



// Helper Component for Data Normalization & Sync
// Extracted to avoid hook rules violation (useMemo inside map)
const SyncedIndicatorWrapper = memo(({
    ind,
    data,
    calculateIndicators,
    isDarkMode,
    onCrosshairMove,
    onSeriesCreated,
    resolveEnrichedPayload,
    syncService,
    externalCrosshair,
    tickMarkFormatter: parentTickMarkFormatter,
    timeFormatter: parentTimeFormatter,
    compact = false,
    onActivate,
}) => {
    const rawData = expandScalarPoints(calculateIndicators(ind, data), ind.styleOptions?.scalarOutputKey); //* 142-37 mixed scalar/dict history
    console.log(`[SyncedIndicatorWrapper] ${ind.id}: rawData.length=${rawData?.length} data.length=${data?.length}`);
    if (rawData?.length > 0 && data?.length > 0) {
        console.log(`[SyncedIndicatorWrapper] ${ind.id} Time overlap check: rawData[0].time=${rawData[0].time} data[0].time=${data[0].time}`);
    }

    const paddedData = useMemo(() => {
        if (!data || data.length === 0) return [];
        // If no indicator data, we still need to return whitespace for every candle to maintain index alignment
        // Otherwise the pane might effectively 'collapse' or misbehave if we return empty.
        // Returning full whitespace array ensures X-axis matches exactly.
        const effectiveRaw = rawData || []; 

        // Index the indicator data by time for fast lookup
        // Use a Map for O(1) access
        const indMap = new Map();
        effectiveRaw.forEach(d => {
             if (d && d.time) indMap.set(d.time, d.value);
        });

        // MASTER LOOP: Iterate over MAIN CHART candles (data) to enforce 1:1 timeline
        // ALIGNMENT FIX: Include whitespace to ensure index matches Main Chart exactly
        const result = data.map(candle => {
            const val = indMap.get(candle.time);
            if (val !== undefined && val !== null) {
                return { time: candle.time, value: val };
            }
            // PADDING: Return time-only object
            return { time: candle.time };
        });
        const withValue = result.filter(d => d.value !== undefined);
        console.log(`[SYNC-DEBUG] SyncedWrapper[${ind.id}] paddedData: total=${result.length}, withValue=${withValue.length}, firstValueIdx=${result.findIndex(d => d.value !== undefined)}, lastValueIdx=${result.length - 1 - [...result].reverse().findIndex(d => d.value !== undefined)}`);
        return result;
    }, [rawData, data]);

    const handleLocalCrosshairMove = useCallback((p) => {
        const enriched = resolveEnrichedPayload(p, ind.id);
        if (enriched && onCrosshairMove) {
            onCrosshairMove(enriched);
            if (syncService) syncService.emit(enriched);
        } else if (!enriched && onCrosshairMove) {
            onCrosshairMove(null);
            if (syncService) syncService.emit(null);
        }
    }, [resolveEnrichedPayload, ind.id, onCrosshairMove, syncService]);

    return (
        <IndicatorPane
            id={ind.id}
            type={ind.type}
            data={paddedData}
            mainChartData={data}
            height={PANE_HEIGHT}
            isDarkMode={isDarkMode}
            onCrosshairMove={handleLocalCrosshairMove}
            onSeriesCreated={onSeriesCreated}
            externalCrosshair={externalCrosshair}
            syncService={syncService}
            color={ind.styleOptions?.color}
            styleOptions={ind.styleOptions}
            tickMarkFormatter={parentTickMarkFormatter}
            timeFormatter={parentTimeFormatter}
            compact={compact}
            onActivate={onActivate}
        />
    );
});


/**
 * AttachedModeManager
 * Synchronizes Trend Slope results with their source indicator series.
 * Enables "Attached Mode" visuals like trend markers on the parent series.
 */
const AttachedModeManager = ({ 
    activeIndicators, 
    calculateIndicators, 
    data, 
    indicatorSeriesMap,
    indicatorIdToSeriesMap,
    edges,
    mainSeriesRef,
    registryVersion,
    attachedMarkersRef,
    onMarkersBuilt
}) => {
    const markerIndicators = useMemo(() => 
        (activeIndicators || []).filter(ind => {
            const markerType = (ind.styleOptions?.type || ind.styleOptions?.chartType || '').toLowerCase();
            return (
                ind.type === 'dee_trend_slope' ||
                ['comparator', 'logic_gate', 'signal', 'position_trigger'].includes(ind.nodeType) ||
                markerType === 'marker'
            );
        }),
    [activeIndicators]);

    // Track which series have active marker plugins to facilitate cleanup
    const activePluginsRef = useRef(new Set()); // Set<Series>

    useEffect(() => {
        if (!markerIndicators.length || !data || data.length === 0) {
            // Clear ALL active marker plugins if no markers are active
            activePluginsRef.current.forEach(series => {
                if (series._markersPlugin) { series._markersPlugin.setMarkers([]); markerSigByPlugin.set(series._markersPlugin, ''); }
            });
            activePluginsRef.current.clear();
            //* QA2-ARCH-1: dropping published markers must relayout so stale angle hover entries go
            const hadAttached = !!attachedMarkersRef && attachedMarkersRef.current.size > 0;
            if (attachedMarkersRef) attachedMarkersRef.current = new Map();
            if (hadAttached && onMarkersBuilt) onMarkersBuilt();
            return;
        }

        // 1. Group markers by target series
        const seriesMarkersMap = new Map(); // series -> markers[]

        markerIndicators.forEach(indNode => {
            const settings = indNode.styleOptions?.markerSettings || {};
            const sourceId = indNode.sourceId || indNode.data?.sourceId;
            
            let targetSeriesArray = sourceId ? indicatorIdToSeriesMap.current.get(sourceId) : null;
            let targetSeries = targetSeriesArray && targetSeriesArray.length > 0 ? targetSeriesArray[0] : null;
            
                // Fallback 1: Edge-based visual connection
            if (!targetSeries && sourceId && edges) {
                const connectedVisualEdge = edges.find(e => e.source === sourceId && indicatorIdToSeriesMap.current.has(e.target));
                if (connectedVisualEdge) {
                    const fallbackArray = indicatorIdToSeriesMap.current.get(connectedVisualEdge.target);
                    targetSeries = fallbackArray && fallbackArray.length > 0 ? fallbackArray[0] : null;
                }
            }

            if (!targetSeries) {
                targetSeries = mainSeriesRef?.current;
            }

            if (!targetSeries) return;

            // Initialize aggregated list for this series
            if (!seriesMarkersMap.has(targetSeries)) {
                seriesMarkersMap.set(targetSeries, []);
            }

            // If hidden or signal markers disabled, don't contribute any markers
            if (indNode.isHidden) return;
            if (settings.enabled === false) return;

            // 2. Calculate markers for this indicator
            const rawIndData = calculateIndicators(indNode, data);
            if (!rawIndData || rawIndData.length === 0) return;

            // 3. Deduplicate: fuzzy-match alignment (±1 interval) can produce identical
            //    values on adjacent candles (e.g. 30m slope on 1m chart → 3 duplicates).
            //    For markers, only keep the FIRST candle of each consecutive value run.
            const dedupedData = rawIndData.filter((d, i) => {
                const currentValue = d?.value;
                if (currentValue === null || currentValue === undefined) return false;
                if (i === 0) return true;
                const prev = rawIndData[i - 1];
                const prevValue = prev?.value;
                if (prevValue === null || prevValue === undefined) return true;
                if (prevValue === currentValue) return false;
                if (typeof currentValue === 'object' && typeof prevValue === 'object' &&
                    currentValue.marker && prevValue.marker && currentValue.marker === prevValue.marker) return false;
                if (typeof currentValue === 'object' && typeof prevValue === 'object' &&
                    typeof currentValue.triggered === 'boolean' && typeof prevValue.triggered === 'boolean' &&
                    currentValue.triggered === prevValue.triggered) return false;
                return true;
            });
            if (dedupedData.length === 0) return;

            const statesConfig = resolveMarkerStates(indNode.visual_settings, indNode.styleOptions);

            //--> Pre-compute invariant state checks OUTSIDE the hot loop (perf: O(1) vs O(N*M))
            const hasBullishState = statesConfig.some(s => s.key === 'bullish');

            const markers = dedupedData.reduce((acc, d) => {
                let stateKey = null;
                let textExtra = null;
                const rawValue = d?.value;
                const scalarValue = (rawValue && typeof rawValue === 'object' && rawValue.value !== undefined)
                    ? rawValue.value
                    : rawValue;
                
                // 1. Trend slope
                if (indNode.type === 'dee_trend_slope') {
                    if (rawValue && typeof rawValue === 'object' && rawValue.marker) {
                        const meta = rawValue.marker.split(':');
                        stateKey = meta[0]; // 'up', 'down', 'sideways'
                        const strength = meta[1];
                        const angle = meta[2];
                        if (settings.showText !== false) {
                            const strVal = strength ? Number(strength).toFixed(1) : '';
                            textExtra = stateKey === 'up' ? `↑ ${angle}° (${strVal})` : (stateKey === 'down' ? `↓ ${angle}° (${strVal})` : `→ (${strVal})`);
                        }
                    }
                } 
                // 1b. Rich signal payload (e.g. { triggered, side, stage, entry_price })
                else if (rawValue && typeof rawValue === 'object' && typeof rawValue.triggered === 'boolean') {
                    stateKey = rawValue.triggered ? 'true' : 'false';
                    if (settings.showText !== false && rawValue.triggered) {
                        const sideLabel = String(rawValue.side || '').toUpperCase();
                        const stageLabel = rawValue.stage != null ? ` S${rawValue.stage}` : '';
                        textExtra = sideLabel ? `${sideLabel}${stageLabel}` : null;
                    }
                }
                // 2. Numeric processing (Pattern vs Boolean)
                else if (typeof scalarValue === 'number') {
                    
                    if (hasBullishState) {
                        if (scalarValue > 0) stateKey = 'bullish';
                        else if (scalarValue < 0) stateKey = 'bearish';
                        else return acc; // 0 means no pattern found. Skip.
                    } else {
                        // Standard logic/boolean indicator
                        if (scalarValue === 1 || scalarValue === 0) {
                            stateKey = scalarValue === 1 ? 'true' : 'false';
                        }
                    }
                }
                // 3. Strict Boolean
                else if (typeof scalarValue === 'boolean') {
                    stateKey = scalarValue ? 'true' : 'false';
                }
                
                if (!stateKey) return acc;

                // Lookup State Def
                const stateDef = statesConfig.find(s => s.key === stateKey) || { key: stateKey, color: '#03c9d7', shape: 'circle', position: 'belowBar', enabled: true };
                
                // Active Check
                const showStateKey = `show_${stateKey}`;
                const isBackwardCompatFalse = stateKey === 'false' && settings.showFalseMarkers !== undefined;
                const isActive = isBackwardCompatFalse ? settings.showFalseMarkers : (settings[showStateKey] !== undefined ? settings[showStateKey] : (stateDef.enabled !== false));

                if (!isActive) return acc;

                // Build Final Styling
                // First check markerSettings overrides, then fallback to metadata defaults
                const color = settings[`${stateKey}Color`] || stateDef.color || '#10B981';
                const shape = settings[`${stateKey}Shape`] || stateDef.shape || 'arrowUp';
                const position = settings[`${stateKey}Position`] || stateDef.position || 'belowBar';
                
                let finalLabel;
                if (settings.showText !== false) {
                    if (textExtra) {
                        finalLabel = textExtra;
                    } else {
                        finalLabel = indNode.label || indNode.params?.label || indNode.type;
                    }
                }

                acc.push({
                    time: d.time,
                    position: position,
                    color: color,
                    shape: shape,
                    text: finalLabel,
                    size: settings.size ?? 1
                });

                return acc;
            }, []);

            // Add to aggregated list
            const currentAggregated = seriesMarkersMap.get(targetSeries);
            seriesMarkersMap.set(targetSeries, [...currentAggregated, ...markers]);
        });

        // 3. Apply markers + cleanup in a SINGLE rAF to prevent blink.
        //    Previous approach: cleanup ran synchronously, marker setting in separate rAFs
        //    → 1-frame gap where markers were cleared but not yet re-set (visible blink).
        const rafId = requestAnimationFrame(() => {
            try {
                // A. 50-13: publish the sorted markers per series to the ONE layout path (SmartChart relayout)
                //*   which creates / updates each plugin through layoutMarkerLabels.
                const published = new Map();
                seriesMarkersMap.forEach((markers, targetSeries) => {
                    const sortedMarkers = [...markers].sort((a, b) => {
                        const timeA = typeof a.time === 'string' ? new Date(a.time).getTime() : a.time;
                        const timeB = typeof b.time === 'string' ? new Date(b.time).getTime() : b.time;
                        return timeA - timeB;
                    });
                    published.set(targetSeries, sortedMarkers);
                    activePluginsRef.current.add(targetSeries);
                });
                if (attachedMarkersRef) attachedMarkersRef.current = published;

                // B. Cleanup stale series (same frame — no blink)
                activePluginsRef.current.forEach(series => {
                    if (!seriesMarkersMap.has(series)) {
                        if (series._markersPlugin) {
                            series._markersPlugin.setMarkers([]);
                            markerSigByPlugin.set(series._markersPlugin, '');
                        }
                        activePluginsRef.current.delete(series);
                    }
                });
                if (onMarkersBuilt) onMarkersBuilt();
            } catch (e) {
                console.error("[AttachedModeManager] Error setting markers:", e);
            }
        });

        return () => cancelAnimationFrame(rafId);

    }, [
        markerIndicators, 
        data, 
        calculateIndicators, 
        indicatorIdToSeriesMap, 
        registryVersion,
        edges, 
        mainSeriesRef, 
        // Force update on style or visibility changes
        JSON.stringify(markerIndicators.map(i => ({ 
            id: i.id, 
            style: i.styleOptions, 
            hidden: i.isHidden 
        })))
    ]); 

    return null;
};

// Helper to convert hex to rgba with opacity
function hexToRgba(hex, opacity = 100) {
    if (!hex) return hex;
    if (hex.startsWith('rgba')) return hex;
    
    let c;
    if(/^#([A-Fa-f0-9]{3}){1,2}$/.test(hex)){
        c= hex.substring(1).split('');
        if(c.length== 3){
            c= [c[0], c[0], c[1], c[1], c[2], c[2]];
        }
        c= '0x'+c.join('');
        return 'rgba('+[(c>>16)&255, (c>>8)&255, c&255].join(',')+','+(opacity/100)+')';
    }
    return hex;
}

export default React.memo(ChartPane);
